import L from "leaflet";
import { buildTinTerrainFromPoints } from "../../simulator/terrain-data.js";

const CAP = 10000;
const ETOPO_IMAGE_URL = "https://gis.ngdc.noaa.gov/arcgis/rest/services/DEM_mosaics/DEM_global_mosaic/ImageServer";
const REFERENCE_INLINE_LIMIT = 2500;
const SAMPLE_CONCURRENCY = 8;
const SAMPLE_UI_BATCH = 50;
const MIN_SAMPLE_CONCURRENCY = 1;
const MAX_SAMPLE_CONCURRENCY = 16;
const EARTH_METERS_PER_DEG_LAT = 111_132;

export const etopoSoundingTool = {
  id: "etopo-sounding",
  name: "Soundings",
  init(app) { this._rt = this._rt ?? createRuntime(app); },
  destroy() { this._rt?.destroy(); this._rt = null; },
  renderPanel(app, containerEl) { if (!this._rt) this._rt = createRuntime(app); this._rt.mount(containerEl); },
  serialize(app, options) { return this._rt?.snapshot?.(options) ?? null; },
  restore(app, data, options) { if (!this._rt) this._rt = createRuntime(app); return this._rt.restore?.(data, options); },
  resolveReference(app, referenceId, file) { if (!this._rt) this._rt = createRuntime(app); return this._rt.resolveReference?.(referenceId, file); },
};

function createRuntime(app) {
  const map = app.map.instance;
  const mapEl = app.map.el;
  const pane = map.createPane("soundingsPane");
  pane.style.zIndex = "800";
  pane.classList.add("soundings-pane");
  const soundingLayer = L.featureGroup([], { pane: "soundingsPane" }).addTo(map);
  const drawLayer = L.featureGroup([], { pane: "soundingsPane" }).addTo(map);

  let container = null;
  let mode = "";
  let workflowMode = ""; // "" | generate | import
  let running = false;
  let drawActive = false;
  let vertices = [];
  let drawHandlers = [];
  let interactionKeyHandler = null;
  let seq = 1;
  let layerSeq = 1;
  let soundingPrecision = 1;
  let spacingMeters = 5;
  let sampleConcurrency = SAMPLE_CONCURRENCY;
  let unitMode = "m";
  let skipLand = true;
  let declutterLabels = true;
  let declutterSensitivity = 50;
  let soundingFont = "default";
  let soundingScale = 1;
  let soundingRotation = 0;
  let selectedSource = "etopo";
  let writeMode = "append";
  let renderMode = "auto"; // auto | points | labels
  let importSourceCRS = "EPSG:4326";
  let importZoom = true;
  let colorScale = "bathy";
  let colorInvert = false;
  let colorClamp = true;
  let colorGamma = false;
  let colorDomainAuto = true;
  let colorDomainMin = null;
  let colorDomainMax = null;
  let colorSolid = "#00ffff";

  let soundingLayers = [];
  let activeLayerId = null;
  let importing = false;
  let pointCanvasLayer = null;
  let viewRefreshOff = null;
  let offActiveSelect = null;
  let offLayerRename = null;
  let offCatalogRequest = null;
  let offExternalImport = null;
  let lastExternalImportSig = "";
  let lastExternalImportTs = 0;
  let currentColorDomain = { min: 0, max: 100 };
  let generationProgress = {
    visible: false,
    ratio: 0,
    countText: "0 / 0",
  };
  let inGlobalSync = false;
  const unresolvedRefs = new Map();
  const MAX_LABELS_VIEWPORT = 2000;
  const DEDUP_DECIMALS = 6;
  const el = {};

  function $(id) { return container?.querySelector?.(`#${id}`); }
  function setStatus(msg) { if (el.status) el.status.textContent = msg; }
  function setGenerationProgress(visible, ratio = 0, countText = "") {
    generationProgress.visible = !!visible;
    generationProgress.ratio = Math.max(0, Math.min(1, Number(ratio) || 0));
    if (countText) generationProgress.countText = String(countText);
    const wrap = $("snd-gen-progress-wrap");
    const bar = $("snd-gen-progress-bar");
    const pctEl = $("snd-gen-progress-pct");
    const countEl = $("snd-gen-progress-count");
    if (!wrap || !bar || !pctEl || !countEl) return;
    wrap.style.display = generationProgress.visible ? "block" : "none";
    const pct = Math.max(0, Math.min(100, Math.round(generationProgress.ratio * 100)));
    bar.style.width = `${pct}%`;
    pctEl.textContent = `${pct}%`;
    countEl.textContent = generationProgress.countText;
  }
  function setImportProgress(visible, ratio = 0, msg = "") {
    const wrap = $("snd-import-progress-wrap");
    const bar = $("snd-import-progress-bar");
    const label = $("snd-import-progress-label");
    if (!wrap || !bar || !label) return;
    wrap.style.display = visible ? "block" : "none";
    const pct = Math.max(0, Math.min(100, Math.round((Number(ratio) || 0) * 100)));
    bar.style.width = `${pct}%`;
    label.textContent = msg || `${pct}%`;
  }

  function yieldUi() {
    return new Promise((resolve) => {
      if (typeof window !== "undefined" && typeof window.requestAnimationFrame === "function") {
        window.requestAnimationFrame(() => resolve());
        return;
      }
      setTimeout(resolve, 0);
    });
  }
  function esc(v) { return String(v ?? "").replaceAll("&", "&amp;").replaceAll("<", "&lt;").replaceAll(">", "&gt;").replaceAll("\"", "&quot;"); }

  function bindMap(type, handler) { map.on(type, handler); drawHandlers.push(() => map.off(type, handler)); }
  function clearMapHandlers() { drawHandlers.splice(0).forEach((off) => { try { off(); } catch {} }); }
  function resetDrawing() { drawActive = false; vertices = []; drawLayer.clearLayers(); clearMapHandlers(); }
  function stopInteraction(withStatus = true) {
    running = false;
    resetDrawing();
    setGenerationProgress(false, 0, "");
    mapEl.style.cursor = "";
    if (interactionKeyHandler) {
      document.removeEventListener("keydown", interactionKeyHandler);
      interactionKeyHandler = null;
    }
    setTimeout(() => map.doubleClickZoom?.enable?.(), 0);
    if (withStatus) setStatus("stopped");
  }

  function createLayer(opts = {}) {
    const layer = {
      id: opts.id || `snd_layer_${Date.now().toString(36)}_${layerSeq++}`,
      name: opts.name || `Soundings ${layerSeq - 1}`,
      sourceType: opts.sourceType || "generated",
      source: opts.source || selectedSource,
      sourceMeta: opts.sourceMeta && typeof opts.sourceMeta === "object" ? { ...opts.sourceMeta } : null,
      visible: opts.visible !== false,
      opacity: Number.isFinite(Number(opts.opacity)) ? Number(opts.opacity) : 1,
      labelRotation: Number.isFinite(Number(opts.labelRotation)) ? (((Number(opts.labelRotation) % 360) + 360) % 360) : (((Number(soundingRotation) % 360) + 360) % 360),
      labelScale: Number.isFinite(Number(opts.labelScale)) ? Math.max(0.5, Math.min(3, Number(opts.labelScale))) : Math.max(0.5, Math.min(3, Number(soundingScale) || 1)),
      records: Array.isArray(opts.records) ? opts.records : [],
    };
    soundingLayers.push(layer);
    registerGlobalLayer(layer);
    syncGlobalLayer(layer);
    if (!activeLayerId) activeLayerId = layer.id;
    emitActiveLayerChanged();
    emitSoundingsCatalogChanged();
    return layer;
  }

  function ensureActiveLayer(createIfMissing = false) {
    let layer = soundingLayers.find((x) => x.id === activeLayerId);
    if (!layer && createIfMissing) layer = createLayer({ name: "Soundings 1", source: selectedSource });
    if (layer) activeLayerId = layer.id;
    return layer;
  }

  function getActiveLayer() { return soundingLayers.find((x) => x.id === activeLayerId) || null; }
  function layerStyleForId(layerId) {
    const layer = soundingLayers.find((x) => x.id === layerId) || null;
    return {
      rotation: Number.isFinite(Number(layer?.labelRotation)) ? (((Number(layer.labelRotation) % 360) + 360) % 360) : (((Number(soundingRotation) % 360) + 360) % 360),
      scale: Number.isFinite(Number(layer?.labelScale)) ? Math.max(0.5, Math.min(3, Number(layer.labelScale))) : Math.max(0.5, Math.min(3, Number(soundingScale) || 1)),
    };
  }
  function syncStyleControlsFromActiveLayer() {
    const layer = getActiveLayer();
    const rot = Number.isFinite(Number(layer?.labelRotation))
      ? (((Number(layer.labelRotation) % 360) + 360) % 360)
      : (((Number(soundingRotation) % 360) + 360) % 360);
    const scale = Number.isFinite(Number(layer?.labelScale))
      ? Math.max(0.5, Math.min(3, Number(layer.labelScale)))
      : Math.max(0.5, Math.min(3, Number(soundingScale) || 1));
    if (el.rotSlider) el.rotSlider.value = `${Math.round(rot)}`;
    if (el.rotNum) el.rotNum.value = `${Math.round(rot)}`;
    if (el.sizeSlider) el.sizeSlider.value = `${scale}`;
    if (el.sizeNum) el.sizeNum.value = `${scale}`;
  }
  function emitSoundingsCatalogChanged() {
    app.events.emit("soundings:catalog:changed", {
      layers: soundingLayers.map((l) => ({
        layerId: l.id,
        globalLayerId: l.globalLayerId || null,
        name: l.name,
        sourceType: l.sourceType,
        source: l.source,
        visible: l.visible !== false,
        opacity: Number.isFinite(Number(l.opacity)) ? Number(l.opacity) : 1,
        featureCount: Array.isArray(l.records) ? l.records.length : 0,
        records: (Array.isArray(l.records) ? l.records : []).map((r) => ({
          id: r.id,
          lat: Number(r.lat),
          lon: Number(r.lon),
          z: Number(r.depth_m),
          ts: r.ts || null,
        })),
      })),
    });
  }
  function emitActiveLayerChanged() {
    const layer = getActiveLayer();
    app.events.emit("soundings:active-layer:changed", {
      layerId: layer?.id ?? null,
      globalLayerId: layer?.globalLayerId ?? null,
    });
  }
  function totalCount() { return soundingLayers.reduce((n, l) => n + l.records.length, 0); }
  function layerById(id) { return soundingLayers.find((x) => x.id === id); }
  function getVisibleRecords() {
    return soundingLayers.filter((l) => l.visible).flatMap((l) => l.records.map((r) => ({ ...r, _opacity: l.opacity ?? 1, _layerId: l.id })));
  }

  function syncGlobalLayer(layer) {
    if (!layer?.globalLayerId) return;
    if (inGlobalSync) return;
    app.services.layers.updateLayer(layer.globalLayerId, {
      name: layer.name,
      visible: layer.visible,
      opacity: layer.opacity ?? 1,
      group: "Soundings",
      meta: { featureCount: layer.records.length, source: layer.source, sourceType: layer.sourceType },
    });
  }

  function registerGlobalLayer(layer) {
    if (!layer || layer.globalLayerId) return;
    layer.globalLayerId = app.services.layers.addLayer("etopo-sounding", {
      name: layer.name,
      kind: "overlay",
      group: "Soundings",
      type: "managed",
      visible: layer.visible,
      opacity: layer.opacity ?? 1,
      volatile: true,
      meta: { featureCount: layer.records.length, source: layer.source, sourceType: layer.sourceType },
      managed: {
        setVisible(v) {
          layer.visible = !!v;
          if (!inGlobalSync) {
            redrawSoundings();
            render();
          }
          emitSoundingsCatalogChanged();
        },
        setOpacity(v) {
          layer.opacity = Math.max(0, Math.min(1, Number(v) || 0));
          if (!inGlobalSync) redrawSoundings();
          emitSoundingsCatalogChanged();
        },
        getBounds() {
          if (!layer.records.length) return null;
          const b = L.latLngBounds(layer.records.map((r) => [r.lat, r.lon]));
          return b?.isValid?.() ? b : null;
        },
        bringToFront() {
          // Soundings tool controls its own draw order.
        },
        remove() {
          if (inGlobalSync) return;
          inGlobalSync = true;
          try { deleteLayer(layer.id, { fromGlobal: true }); } finally { inGlobalSync = false; }
        },
      },
    });
  }

  function shouldUseLabels() {
    if (renderMode === "labels") return true;
    if (renderMode === "points") return false;
    return map.getZoom() >= 15;
  }

  function unitsFactor(units) { return units === "ft" ? 3.28084 : units === "fm" ? 0.546807 : 1; }
  function fontFamilyFor(key) {
    if (key === "trebuchet") return "'Trebuchet MS', 'Segoe UI', sans-serif";
    if (key === "arial") return "Arial, Helvetica, sans-serif";
    if (key === "mono") return "Consolas, 'Courier New', monospace";
    if (key === "mono2") return "'Lucida Console', Monaco, monospace";
    return "system-ui, -apple-system, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif";
  }
  function formatDepth(depthM) {
    const factor = unitsFactor(unitMode || "m");
    return Number(Number(depthM) * factor).toFixed(soundingPrecision);
  }

  function setRotation(deg) {
    soundingRotation = ((Number(deg) % 360) + 360) % 360;
    const layer = getActiveLayer();
    if (layer) layer.labelRotation = soundingRotation;
    if (el.rotSlider) el.rotSlider.value = `${Math.round(soundingRotation)}`;
    if (el.rotNum) el.rotNum.value = `${Math.round(soundingRotation)}`;
    redrawSoundings();
    emitSoundingsCatalogChanged();
  }

  function setSize(scale) {
    soundingScale = Math.max(0.5, Math.min(3, Number(scale) || 1));
    const layer = getActiveLayer();
    if (layer) layer.labelScale = soundingScale;
    if (el.sizeSlider) el.sizeSlider.value = `${soundingScale}`;
    if (el.sizeNum) el.sizeNum.value = `${soundingScale}`;
    redrawSoundings();
    emitSoundingsCatalogChanged();
  }

  function renderModeButtons() {
    container?.querySelectorAll?.(".snd-mode-btn")?.forEach?.((btn) => btn.classList.toggle("active", btn.dataset.mode === mode));
    ["single", "section", "grid"].forEach((k) => { const panel = $(`snd-panel-${k}`); if (panel) panel.style.display = k === mode ? "block" : "none"; });
  }

  async function fetchEtopoMSL(ll) {
    try {
      const v = await app.services.identify.sampleDepth(ll.lng, ll.lat, "etopo");
      if (Number.isFinite(Number(v?.depth_m))) return Number(v.depth_m);
    } catch {}
    const g = encodeURIComponent(JSON.stringify({ x: ll.lng, y: ll.lat, spatialReference: { wkid: 4326 } }));
    const url = `${ETOPO_IMAGE_URL}/identify?f=json&geometry=${g}&geometryType=esriGeometryPoint&sr=4326&returnGeometry=false`;
    const d = await (await fetch(url)).json();
    if (d?.properties && typeof d.properties.value !== "undefined") return Number(d.properties.value);
    if (typeof d?.value !== "undefined") return Number(d.value);
    if (typeof d?.pixelValue !== "undefined") return Number(d.pixelValue);
    return null;
  }

  async function sampleDepth(ll, source) {
    const src = String(source || "etopo").toLowerCase();
    if (src === "etopo") return fetchEtopoMSL(ll);
    try {
      const v = await app.services.identify.sampleDepth(ll.lng, ll.lat, src);
      if (Number.isFinite(Number(v?.depth_m))) return Number(v.depth_m);
    } catch {}
    return null;
  }

  function buildIcon(depthM, layerId) {
    const style = layerStyleForId(layerId);
    const txt = formatDepth(depthM);
    const parts = txt.split(".");
    const left = parts[0];
    const right = parts[1] ?? "0".repeat(soundingPrecision);
    const svg = `
      <svg xmlns="http://www.w3.org/2000/svg" overflow="visible" data-depth-m="${Number(depthM)}" data-layer-id="${esc(layerId)}">
        <g class="snd" style="transform: rotate(${Number(style.rotation).toFixed(2)}deg) scale(${Number(style.scale).toFixed(3)}); transform-origin: 0 0;">
          <text y="0" dominant-baseline="middle" font-weight="800" font-size="12" fill="#ffffff" stroke="#000000" stroke-width="0.85" paint-order="stroke fill" style="font-family:${fontFamilyFor(soundingFont)}; font-variant-numeric: tabular-nums; font-feature-settings: 'tnum' 1;">
            <tspan x="0" text-anchor="end">${left}</tspan>
            <tspan x="0" text-anchor="middle">.</tspan>
            <tspan x="0" text-anchor="start">${right}</tspan>
          </text>
        </g>
      </svg>`;
    return L.divIcon({ className: "sounding-label", html: svg, iconSize: [0, 0], iconAnchor: [0, 0] });
  }

  function ensurePointCanvasLayer() {
    if (pointCanvasLayer) return pointCanvasLayer;
    const paneEl = map.getPane("soundingsPane");
    const canvas = document.createElement("canvas");
    canvas.className = "snd-points-canvas";
    canvas.style.position = "absolute";
    canvas.style.pointerEvents = "none";
    canvas.style.zIndex = "790";
    paneEl.appendChild(canvas);
    const ctx = canvas.getContext("2d");
    pointCanvasLayer = {
      canvas,
      ctx,
      visible: true,
      setVisible(v) { this.visible = !!v; canvas.style.display = this.visible ? "block" : "none"; },
      remove() { try { canvas.remove(); } catch {} },
      redraw() {
        if (!this.visible) return;
        const size = map.getSize();
        const dpr = window.devicePixelRatio || 1;
        if (canvas.width !== Math.round(size.x * dpr) || canvas.height !== Math.round(size.y * dpr)) {
          canvas.width = Math.round(size.x * dpr);
          canvas.height = Math.round(size.y * dpr);
          canvas.style.width = `${size.x}px`;
          canvas.style.height = `${size.y}px`;
        }
        const topLeft = map.containerPointToLayerPoint([0, 0]);
        L.DomUtil.setPosition(canvas, topLeft);
        ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
        ctx.clearRect(0, 0, size.x, size.y);
        const b = map.getBounds().pad(0.2);
        const radius = Math.max(1, Math.min(4, 1.2 * soundingScale));
        const recs = getVisibleRecords();
        for (let i = 0; i < recs.length; i += 1) {
          const r = recs[i];
          const ll = L.latLng(r.lat, r.lon);
          if (!b.contains(ll)) continue;
          const pt = map.latLngToContainerPoint(ll);
          ctx.beginPath();
          ctx.arc(pt.x, pt.y, radius, 0, Math.PI * 2);
          ctx.fillStyle = depthColor(r.depth_m);
          ctx.globalAlpha = 0.9 * Math.max(0, Math.min(1, Number(r._opacity) || 1));
          ctx.fill();
        }
      },
    };
    return pointCanvasLayer;
  }

  function visibleLabelRecords() {
    const recs = getVisibleRecords();
    const b = map.getBounds().pad(0.2);
    const zoom = map.getZoom();
    const zoomTighten = Math.max(0, 17 - zoom);
    const sensT = Math.max(0, Math.min(100, Number(declutterSensitivity) || 0)) / 100;
    const sensFactor = 0.35 + (sensT * 1.9);
    const minSepPx = Math.max(8, Math.round((38 + zoomTighten * 3) * soundingScale * sensFactor));
    const maxLabels = MAX_LABELS_VIEWPORT;
    const sep2 = minSepPx * minSepPx;

    const candidates = [];
    for (let i = 0; i < recs.length; i += 1) {
      const r = recs[i];
      const ll = L.latLng(r.lat, r.lon);
      if (!b.contains(ll)) continue;
      const p = map.latLngToContainerPoint(ll);
      // Deterministic pseudo-random priority from position avoids row/column bias.
      const hx = Math.round((Number(r.lon) || 0) * 1e6);
      const hy = Math.round((Number(r.lat) || 0) * 1e6);
      let h = ((hx * 73856093) ^ (hy * 19349663)) >>> 0;
      h ^= h << 13; h ^= h >>> 17; h ^= h << 5;
      const prio = (h >>> 0) / 0xffffffff;
      candidates.push({ r, x: p.x, y: p.y, prio });
    }
    candidates.sort((a, b2) => a.prio - b2.prio);

    if (!declutterLabels) {
      return candidates.slice(0, maxLabels).map((c) => c.r);
    }

    const buckets = new Map();
    const kept = [];
    const bucketSize = minSepPx;
    for (let i = 0; i < candidates.length; i += 1) {
      const c = candidates[i];
      const bx = Math.floor(c.x / bucketSize);
      const by = Math.floor(c.y / bucketSize);
      let blocked = false;
      for (let yy = by - 1; yy <= by + 1 && !blocked; yy += 1) {
        for (let xx = bx - 1; xx <= bx + 1; xx += 1) {
          const arr = buckets.get(`${xx},${yy}`);
          if (!arr) continue;
          for (let j = 0; j < arr.length; j += 1) {
            const k = arr[j];
            const dx = c.x - k.x;
            const dy = c.y - k.y;
            if (dx * dx + dy * dy < sep2) { blocked = true; break; }
          }
          if (blocked) break;
        }
      }
      if (blocked) continue;
      const k = { x: c.x, y: c.y, r: c.r };
      const key = `${bx},${by}`;
      if (!buckets.has(key)) buckets.set(key, []);
      buckets.get(key).push(k);
      kept.push(c.r);
      if (kept.length >= maxLabels) break;
    }
    return kept;
  }

  function redrawSoundings() {
    ensurePointCanvasLayer();
    let labelsOn = shouldUseLabels();
    if (labelsOn && getVisibleRecords().length > MAX_LABELS_VIEWPORT * 3) labelsOn = false;
    if (pointCanvasLayer) pointCanvasLayer.setVisible(false);
    soundingLayer.clearLayers();
    if (labelsOn) {
      const labelRecs = visibleLabelRecords();
      if (labelRecs.length >= MAX_LABELS_VIEWPORT) {
        setStatus(
          declutterLabels
            ? `label cap reached (${MAX_LABELS_VIEWPORT.toLocaleString()}), thinning applied`
            : `label cap reached (${MAX_LABELS_VIEWPORT.toLocaleString()}), declutter off`,
        );
      }
      labelRecs.forEach((r) => {
        const m = L.marker([r.lat, r.lon], { icon: buildIcon(r.depth_m, r._layerId || "label"), interactive: false, pane: "soundingsPane", opacity: Math.max(0, Math.min(1, Number(r._opacity) || 1)) });
        m.addTo(soundingLayer);
      });
    } else {
      const recs = getVisibleRecords();
      recs.forEach((r) => {
        const style = layerStyleForId(r._layerId);
        const radius = Math.max(1, Math.min(4, 1.2 * Number(style.scale || 1)));
        const cm = L.circleMarker([r.lat, r.lon], {
          pane: "soundingsPane",
          radius,
          stroke: false,
          fill: true,
          fillColor: depthColor(r.depth_m),
          fillOpacity: Math.max(0, Math.min(1, Number(r._opacity) || 1)) * 0.9,
          interactive: false,
        });
        cm._depthM = Number(r.depth_m);
        cm._sndOpacity = Math.max(0, Math.min(1, Number(r._opacity) || 1));
        cm.addTo(soundingLayer);
      });
    }
    applySoundingColors();
  }

  function addToActiveLayer(ll, depthM, options = {}) {
    const layer = ensureActiveLayer(true);
    layer.source = selectedSource;
    layer.records.push({
      id: `snd_${Date.now().toString(36)}_${seq++}`,
      lat: ll.lat,
      lon: ll.lng,
      depth_m: depthM,
      ts: new Date().toISOString(),
      source: layer.source,
    });
    if (options?.suppressUi !== true) {
      syncGlobalLayer(layer);
      redrawSoundings();
      emitSoundingsCatalogChanged();
    }
  }

  function flushLayerUi(layer = getActiveLayer()) {
    if (!layer) return;
    syncGlobalLayer(layer);
    redrawSoundings();
    emitSoundingsCatalogChanged();
  }

  async function samplePointsBuffered(points, totalLabel) {
    const targetLayer = ensureActiveLayer(true);
    if (!targetLayer) return { done: 0, kept: 0, cancelled: false };
    let nextIndex = 0;
    let done = 0;
    let kept = 0;
    let cancelled = false;
    let dirty = false;

    const maybeFlush = async (force = false) => {
      if (!dirty) return;
      if (!force && done > 0 && done % SAMPLE_UI_BATCH !== 0) return;
      flushLayerUi(targetLayer);
      dirty = false;
      await yieldUi();
    };

    const worker = async () => {
      while (nextIndex < points.length) {
        if (!running) {
          cancelled = true;
          return;
        }
        const current = nextIndex;
        nextIndex += 1;
        const p = points[current];
        const v = await sampleDepth(p, selectedSource).catch(() => null);
        if (Number.isFinite(v) && (!skipLand || v < 0)) {
          addToActiveLayer(p, Math.max(0, -v), { suppressUi: true });
          kept += 1;
          dirty = true;
        }
        done += 1;
        if (done % 5 === 0 || done === points.length) {
          setGenerationProgress(true, done / Math.max(1, points.length), `${done.toLocaleString()} / ${totalLabel}`);
        }
        await maybeFlush(false);
      }
    };

    const workers = [];
    const workerCount = Math.max(MIN_SAMPLE_CONCURRENCY, Math.min(MAX_SAMPLE_CONCURRENCY, Number(sampleConcurrency) || SAMPLE_CONCURRENCY, points.length));
    for (let i = 0; i < workerCount; i += 1) workers.push(worker());
    await Promise.all(workers);
    await maybeFlush(true);
    return { done, kept, cancelled };
  }

  function clearActiveLayer() {
    const layer = getActiveLayer();
    if (!layer) { setStatus("no active layer"); return; }
    layer.records = [];
    syncGlobalLayer(layer);
    redrawSoundings();
    render();
    emitSoundingsCatalogChanged();
    setStatus("cleared");
  }

  function clearActiveLayerForReplace() {
    if (writeMode !== "replace") return;
    const layer = getActiveLayer();
    if (!layer) return;
    layer.records = [];
    redrawSoundings();
    emitSoundingsCatalogChanged();
    setStatus("replace mode: active layer cleared");
  }

  function zoomToLayer(id) {
    const layer = layerById(id);
    if (!layer || !layer.records.length) return;
    const bounds = L.latLngBounds(layer.records.map((r) => [r.lat, r.lon]));
    if (bounds.isValid()) map.fitBounds(bounds.pad(0.15));
  }

  function deleteLayer(id, { fromGlobal = false } = {}) {
    const idx = soundingLayers.findIndex((x) => x.id === id);
    if (idx < 0) return;
    const layer = soundingLayers[idx];
    soundingLayers.splice(idx, 1);
    if (!fromGlobal && layer?.globalLayerId) {
      inGlobalSync = true;
      try { app.services.layers.removeLayer(layer.globalLayerId); } finally { inGlobalSync = false; }
    }
    if (activeLayerId === id) {
      activeLayerId = soundingLayers[Math.max(0, idx - 1)]?.id || soundingLayers[0].id;
    }
    redrawSoundings();
    render();
    emitSoundingsCatalogChanged();
    setStatus("layer deleted");
  }

  function collectDepths() {
    return getVisibleRecords().map((r) => Number(r.depth_m)).filter((v) => Number.isFinite(v));
  }

  function lerp(a, b, t) { return a + (b - a) * t; }
  function rgb(r, g, b) { return `rgb(${Math.round(r)},${Math.round(g)},${Math.round(b)})`; }
  function rampBathy(t) { return t < 0.5 ? rgb(lerp(10, 0, t / 0.5), lerp(30, 180, t / 0.5), lerp(80, 255, t / 0.5)) : rgb(lerp(255, 210, (t - 0.5) / 0.5), lerp(255, 220, (t - 0.5) / 0.5), lerp(255, 170, (t - 0.5) / 0.5)); }
  function rampGreys(t) { const v = lerp(20, 235, t); return rgb(v, v, v); }
  function rampViridis(t) { const c = [[68, 1, 84], [59, 82, 139], [33, 145, 140], [94, 201, 98], [253, 231, 37]]; const i = Math.min(c.length - 2, Math.max(0, Math.floor(t * (c.length - 1)))); const u = t * (c.length - 1) - i; return rgb(lerp(c[i][0], c[i + 1][0], u), lerp(c[i][1], c[i + 1][1], u), lerp(c[i][2], c[i + 1][2], u)); }
  function rampCividis(t) { const c = [[0, 32, 77], [58, 82, 139], [90, 119, 170], [127, 158, 185], [233, 229, 132]]; const i = Math.min(c.length - 2, Math.max(0, Math.floor(t * (c.length - 1)))); const u = t * (c.length - 1) - i; return rgb(lerp(c[i][0], c[i + 1][0], u), lerp(c[i][1], c[i + 1][1], u), lerp(c[i][2], c[i + 1][2], u)); }
  function rampTurbo(t) { return rgb(34 + 276 * t, 13 + 242 * (Math.sin(1.6 * t) ** 2), 5 + 250 * (1 - t)); }
  function getRamp(name) { if (name === "viridis") return rampViridis; if (name === "cividis") return rampCividis; if (name === "turbo") return rampTurbo; if (name === "greys") return rampGreys; return rampBathy; }

  function depthColor(depth) {
    const d = Number(depth);
    if (!Number.isFinite(d)) return colorSolid;
    const useSolid = colorScale === "solid";
    if (useSolid) return colorSolid;
    const ramp = getRamp(colorScale);
    const domain = currentColorDomain;
    let t = (d - domain.min) / (domain.max - domain.min);
    if (colorClamp) t = Math.max(0, Math.min(1, t));
    if (colorGamma) t = Math.pow(t, 0.6);
    if (colorInvert) t = 1 - t;
    return ramp(t);
  }

  function updateLegend(domain, ramp, invert) {
    const bar = $("snd-legend");
    if (!bar) return;
    const stops = [];
    for (let i = 0; i <= 30; i += 1) {
      let t = i / 30;
      if (invert) t = 1 - t;
      stops.push(`${ramp(t)} ${Math.round((100 * i) / 30)}%`);
    }
    bar.style.background = `linear-gradient(90deg, ${stops.join(",")})`;
    const span = Math.max(1e-9, domain.max - domain.min);
    const fmt = (v) => (Math.abs(span) >= 5 ? `${Math.round(v)}` : v.toFixed(1));
    if ($("snd-legend-min")) $("snd-legend-min").textContent = fmt(domain.min);
    if ($("snd-legend-q1")) $("snd-legend-q1").textContent = fmt(domain.min + 0.25 * span);
    if ($("snd-legend-mid")) $("snd-legend-mid").textContent = fmt(domain.min + 0.5 * span);
    if ($("snd-legend-q3")) $("snd-legend-q3").textContent = fmt(domain.min + 0.75 * span);
    if ($("snd-legend-max")) $("snd-legend-max").textContent = fmt(domain.max);
  }

  function applySoundingColors() {
    const vals = collectDepths();
    const min = Number.isFinite(colorDomainMin) ? colorDomainMin : (vals.length ? Math.min(...vals) : 0);
    const maxRaw = Number.isFinite(colorDomainMax) ? colorDomainMax : (vals.length ? Math.max(...vals) : 100);
    const domain = { min, max: min === maxRaw ? min + 1e-6 : maxRaw };
    currentColorDomain = domain;
    const useSolid = colorScale === "solid";
    const ramp = getRamp(useSolid ? "bathy" : colorScale);
    updateLegend(domain, ramp, colorInvert);
    if (useSolid && $("snd-legend")) $("snd-legend").style.background = colorSolid;
    document.querySelectorAll(".sounding-label text").forEach((tEl) => {
      const svg = tEl.closest("svg[data-depth-m]");
      const d = svg ? Number(svg.getAttribute("data-depth-m")) : NaN;
      if (!Number.isFinite(d)) return;
      let fill = colorSolid;
      if (!useSolid) {
        let t = (d - domain.min) / (domain.max - domain.min);
        if (colorClamp) t = Math.max(0, Math.min(1, t));
        if (colorGamma) t = Math.pow(t, 0.6);
        if (colorInvert) t = 1 - t;
        fill = ramp(t);
      }
      tEl.setAttribute("fill", fill);
      tEl.setAttribute("stroke", "rgba(0,0,0,0.85)");
    });
    soundingLayer.eachLayer((layer) => {
      if (!(layer instanceof L.CircleMarker)) return;
      const d = Number(layer._depthM);
      if (!Number.isFinite(d)) return;
      layer.setStyle({
        fillColor: depthColor(d),
        fillOpacity: 0.9 * Math.max(0, Math.min(1, Number(layer._sndOpacity) || 1)),
      });
    });
    pointCanvasLayer?.redraw?.();
  }

  function haversine(a, b) {
    const R = 6371000;
    const toRad = (x) => x * Math.PI / 180;
    const dLat = toRad(b.lat - a.lat);
    const dLon = toRad(b.lng - a.lng);
    const la1 = toRad(a.lat);
    const la2 = toRad(b.lat);
    const h = Math.sin(dLat / 2) ** 2 + Math.cos(la1) * Math.cos(la2) * Math.sin(dLon / 2) ** 2;
    return 2 * R * Math.asin(Math.sqrt(h));
  }

  function sampleAlongLine(points, spacing) {
    const out = [];
    if (points.length < 2) return out;
    let segStart = points[0];
    let carry = 0;
    for (let i = 1; i < points.length; i += 1) {
      const segEnd = points[i];
      let segLen = haversine(segStart, segEnd);
      while (carry + segLen >= spacing) {
        const t = (spacing - carry) / segLen;
        const lat = segStart.lat + t * (segEnd.lat - segStart.lat);
        const lng = segStart.lng + t * (segEnd.lng - segStart.lng);
        out.push({ lat, lng });
        segStart = { lat, lng };
        segLen = haversine(segStart, segEnd);
        carry = 0;
      }
      carry += segLen;
      segStart = segEnd;
    }
    return out;
  }

  function pointInPoly(p, vs) {
    let inside = false;
    for (let i = 0, j = vs.length - 1; i < vs.length; j = i++) {
      const xi = vs[i].lng; const yi = vs[i].lat;
      const xj = vs[j].lng; const yj = vs[j].lat;
      const hit = ((yi > p.lat) !== (yj > p.lat)) && (p.lng < ((xj - xi) * (p.lat - yi)) / (yj - yi) + xi);
      if (hit) inside = !inside;
    }
    return inside;
  }

  function gridInsidePolygon(latlngs, spacing) {
    const lats = latlngs.map((p) => p.lat);
    const lngs = latlngs.map((p) => p.lng);
    const minLat = Math.min(...lats), maxLat = Math.max(...lats), minLng = Math.min(...lngs), maxLng = Math.max(...lngs);
    const dlat = spacing / 111320;
    const dlon = spacing / (111320 * Math.cos(((minLat + maxLat) * Math.PI) / 360));
    const pts = [];
    for (let lat = minLat; lat <= maxLat; lat += dlat) for (let lng = minLng; lng <= maxLng; lng += dlon) if (pointInPoly({ lat, lng }, latlngs)) pts.push({ lat, lng });
    return pts;
  }

  async function runSingleClick(e) {
    setStatus("querying...");
    const v = await sampleDepth(e.latlng, selectedSource);
    if (!Number.isFinite(v)) { setStatus("no data"); return; }
    if (skipLand && v >= 0) { setStatus("land skipped"); return; }
    addToActiveLayer(e.latlng, Math.max(0, -v));
    setStatus("placed");
    render();
  }

  async function runSection(latlngs, spacing) {
    const allStations = sampleAlongLine(latlngs, spacing);
    const stations = allStations.slice(0, CAP);
    if (allStations.length > CAP) setStatus(`sampling capped at ${CAP.toLocaleString()} of ${allStations.length.toLocaleString()} points`);
    else setStatus(`sampling ${stations.length.toLocaleString()} points`);
    setGenerationProgress(true, 0, `0 / ${stations.length.toLocaleString()}`);
    const { done, cancelled } = await samplePointsBuffered(stations, stations.length.toLocaleString());
    if (cancelled) {
      setStatus("cancelled");
      setGenerationProgress(false, 0, "");
      return;
    }
    setGenerationProgress(true, 1, `${done.toLocaleString()} / ${stations.length.toLocaleString()}`);
    setStatus(`done ${done.toLocaleString()} / ${stations.length.toLocaleString()}`);
    running = false;
    setGenerationProgress(false, 0, "");
    render();
  }

  async function runGrid(latlngs, spacing) {
    const allPts = gridInsidePolygon(latlngs, spacing);
    const pts = allPts.slice(0, CAP);
    if (allPts.length > CAP) setStatus(`sampling capped at ${CAP.toLocaleString()} of ${allPts.length.toLocaleString()} points`);
    else setStatus(`sampling ${pts.length.toLocaleString()} points`);
    setGenerationProgress(true, 0, `0 / ${pts.length.toLocaleString()}`);
    const { done, cancelled } = await samplePointsBuffered(pts, pts.length.toLocaleString());
    if (cancelled) {
      setStatus("cancelled");
      setGenerationProgress(false, 0, "");
      return;
    }
    setGenerationProgress(true, 1, `${done.toLocaleString()} / ${pts.length.toLocaleString()}`);
    setStatus(`done ${done.toLocaleString()} / ${pts.length.toLocaleString()}`);
    running = false;
    setGenerationProgress(false, 0, "");
    render();
  }

  function beginInteraction() {
    if (workflowMode !== "generate") { setStatus("select Generate mode"); return; }
    if (!getActiveLayer()) { setStatus("create a sounding layer first"); return; }
    if (!["single", "section", "grid"].includes(mode)) { setStatus("select Point, Line, or Grid"); return; }
    stopInteraction(false);
    clearActiveLayerForReplace();
    running = true;
    interactionKeyHandler = (e) => {
      if (e.key !== "Escape") return;
      stopInteraction(false);
      setStatus("cancelled");
      setGenerationProgress(false, 0, "");
      render();
    };
    document.addEventListener("keydown", interactionKeyHandler);
    if (mode === "single") {
      mapEl.style.cursor = "crosshair";
      map.doubleClickZoom?.disable?.();
      bindMap("click", runSingleClick);
      bindMap("dblclick", (e) => {
        L.DomEvent.stop(e);
        stopInteraction(false);
        setStatus("idle");
        render();
      });
      return;
    }

    drawActive = true;
    vertices = [];
    mapEl.style.cursor = "crosshair";
    map.doubleClickZoom?.disable?.();
    const tempLine = L.polyline([], { color: "#22d3ee", weight: 2, opacity: 0.8, dashArray: "4 4" }).addTo(drawLayer);
    const tempPoly = L.polygon([], { color: "#22d3ee", weight: 2, opacity: 0.6, fillOpacity: 0.1 }).addTo(drawLayer);
    const updateTemp = () => { if (mode === "grid") { tempPoly.setLatLngs([vertices]); tempLine.setLatLngs(vertices); } else { tempLine.setLatLngs(vertices); tempPoly.setLatLngs([]); } };
    const onClick = (e) => { if (!drawActive) return; vertices.push(e.latlng); updateTemp(); };
    const onDbl = async (e) => {
      L.DomEvent.stop(e);
      if (!drawActive) return;
      const spacing = Math.max(0.1, Number(spacingMeters || 5));
      const verts = vertices.slice();
      if (e?.latlng) { const last = verts[verts.length - 1]; if (!last || Math.abs(last.lat - e.latlng.lat) > 1e-10 || Math.abs(last.lng - e.latlng.lng) > 1e-10) verts.push(e.latlng); }
      const uniqueCount = new Set(verts.map((v) => `${v.lat.toFixed(7)},${v.lng.toFixed(7)}`)).size;
      drawActive = false;
      resetDrawing();
      mapEl.style.cursor = "";
      setTimeout(() => map.doubleClickZoom?.enable?.(), 0);
      if (mode === "grid" && uniqueCount >= 3) await runGrid(verts, spacing);
      else if (mode === "section" && uniqueCount >= 2) await runSection(verts, spacing);
      else setStatus("Not Enough Vertices");
      running = false;
      render();
    };
    bindMap("click", onClick);
    bindMap("dblclick", onDbl);
  }

  function exportActiveGeoJSON() {
    const layer = getActiveLayer();
    if (!layer) { setStatus("no active layer"); return; }
    const fc = { type: "FeatureCollection", features: layer.records.map((r) => ({ type: "Feature", properties: { id: r.id, depth_m: r.depth_m, ts: r.ts, source: layer.source }, geometry: { type: "Point", coordinates: [r.lon, r.lat] } })) };
    const a = document.createElement("a");
    const u = URL.createObjectURL(new Blob([JSON.stringify(fc, null, 2)], { type: "application/geo+json" }));
    a.href = u;
    a.download = `${layer.name.replaceAll(" ", "_").toLowerCase()}_${Date.now()}.geojson`;
    document.body.appendChild(a);
    a.click();
    setTimeout(() => { URL.revokeObjectURL(u); a.remove(); }, 250);
  }

  function exportActiveXYZ() {
    const layer = getActiveLayer();
    if (!layer) { setStatus("no active layer"); return; }
    const unit = unitMode === "ft" ? "ft" : "m";
    const factor = unitsFactor(unit);
    const rows = (layer.records || [])
      .filter((r) => Number.isFinite(Number(r.lon)) && Number.isFinite(Number(r.lat)) && Number.isFinite(Number(r.depth_m)))
      .map((r) => `${Number(r.lon)} ${Number(r.lat)} ${Number(Number(r.depth_m) * factor).toFixed(soundingPrecision)}`);
    const text = [`# HydroTools soundings export`, `# units=${unit}`, ...rows].join("\n");
    const a = document.createElement("a");
    const u = URL.createObjectURL(new Blob([text], { type: "text/plain;charset=utf-8" }));
    a.href = u;
    a.download = `${layer.name.replaceAll(" ", "_").toLowerCase()}_${Date.now()}.xyz`;
    document.body.appendChild(a);
    a.click();
    setTimeout(() => { URL.revokeObjectURL(u); a.remove(); }, 250);
  }

  function exportActiveSimulatorTin() {
    const layer = getActiveLayer();
    if (!layer) { setStatus("no active layer"); return; }
    const records = Array.isArray(layer.records) ? layer.records : [];
    const valid = records.filter((r) => Number.isFinite(Number(r.lat)) && Number.isFinite(Number(r.lon)) && Number.isFinite(Number(r.depth_m)));
    if (valid.length < 3) {
      setStatus("need at least 3 valid soundings for simulator terrain");
      return;
    }
    const origin = computeLocalOrigin(valid);
    const terrainPoints = valid.map((r) => soundingRecordToLocalPoint(r, origin));
    const terrain = buildTinTerrainFromPoints(terrainPoints);
    const payload = {
      version: 1,
      format: "hydrotools-simulator-terrain",
      terrain: {
        ...terrain,
        vertices: Array.from(terrain.vertices || []),
        triangles: Array.from(terrain.triangles || []),
        binOffsets: Array.from(terrain.binOffsets || []),
        binTriangleIndices: Array.from(terrain.binTriangleIndices || []),
      },
      metadata: {
        source: "hydrotools-etopo-soundings",
        layerId: layer.id,
        layerName: layer.name,
        pointCount: valid.length,
        exportedAt: new Date().toISOString(),
        origin,
      },
    };
    const a = document.createElement("a");
    const u = URL.createObjectURL(new Blob([JSON.stringify(payload)], { type: "application/json" }));
    a.href = u;
    a.download = `${layer.name.replaceAll(" ", "_").toLowerCase()}_${Date.now()}_sim_tin.json`;
    document.body.appendChild(a);
    a.click();
    setTimeout(() => { URL.revokeObjectURL(u); a.remove(); }, 250);
  }

  function computeLocalOrigin(records) {
    let sumLat = 0;
    let sumLon = 0;
    let count = 0;
    for (let i = 0; i < records.length; i += 1) {
      const lat = Number(records[i]?.lat);
      const lon = Number(records[i]?.lon);
      if (!Number.isFinite(lat) || !Number.isFinite(lon)) continue;
      sumLat += lat;
      sumLon += lon;
      count += 1;
    }
    const lat = count ? (sumLat / count) : 0;
    const lon = count ? (sumLon / count) : 0;
    return { lat, lon };
  }

  function soundingRecordToLocalPoint(record, origin) {
    const lat = Number(record.lat);
    const lon = Number(record.lon);
    const depthM = Number(record.depth_m);
    const metersPerDegLon = 111_320 * Math.cos((Number(origin.lat) * Math.PI) / 180);
    return {
      x: (lon - Number(origin.lon)) * metersPerDegLon,
      y: -Math.abs(depthM),
      z: (lat - Number(origin.lat)) * EARTH_METERS_PER_DEG_LAT,
    };
  }

  function exportActive() {
    const fmt = String(el.exportFormat?.value || "geojson").toLowerCase();
    if (fmt === "sim_tin") {
      exportActiveSimulatorTin();
      setStatus("exported Simulator TIN");
      return;
    }
    if (fmt === "xyz") {
      exportActiveXYZ();
      setStatus("exported XYZ");
      return;
    }
    exportActiveGeoJSON();
    setStatus("exported GeoJSON");
  }

  function importCrsOptionsHtml() {
    const svc = app.services?.projections;
    const list = typeof svc?.listCRS === "function" ? svc.listCRS() : [{ code: "EPSG:4326", name: "WGS 84 (Lat/Lon)" }];
    const uniq = new Map();
    list.forEach((c) => { if (c?.code) uniq.set(String(c.code).toUpperCase(), c); });
    if (!uniq.has("EPSG:4326")) uniq.set("EPSG:4326", { code: "EPSG:4326", name: "WGS 84 (Lat/Lon)" });
    return [...uniq.values()].map((c) => `<option value="${esc(c.code)}" ${String(c.code).toUpperCase() === String(importSourceCRS).toUpperCase() ? "selected" : ""}>${esc(c.code)}${c.name ? ` - ${esc(c.name)}` : ""}</option>`).join("");
  }

  function listImportCrsOptions() {
    const svc = app.services?.projections;
    const list = typeof svc?.listCRS === "function" ? svc.listCRS() : [{ code: "EPSG:4326", name: "WGS 84 (Lat/Lon)" }];
    const uniq = new Map();
    list.forEach((c) => { if (c?.code) uniq.set(String(c.code).toUpperCase(), c); });
    if (!uniq.has("EPSG:4326")) uniq.set("EPSG:4326", { code: "EPSG:4326", name: "WGS 84 (Lat/Lon)" });
    return [...uniq.values()];
  }

  function promptImportCrsSelect() {
    return new Promise((resolve) => {
      const options = listImportCrsOptions();
      const overlay = document.createElement("div");
      overlay.style.position = "fixed";
      overlay.style.inset = "0";
      overlay.style.zIndex = "5000";
      overlay.style.background = "rgba(2,6,23,.65)";
      overlay.style.display = "grid";
      overlay.style.placeItems = "center";
      const card = document.createElement("div");
      card.className = "card";
      card.style.width = "min(520px, calc(100vw - 32px))";
      card.style.padding = "12px";
      card.innerHTML = `
        <div style="font-weight:700; margin-bottom:8px;">Select Import Projection</div>
        <div class="small" style="margin-bottom:8px;">Choose CRS for the selected sounding file(s).</div>
        <select id="snd-crs-pick" style="width:100%; margin-bottom:10px;">
          ${options.map((c) => `<option value="${esc(c.code)}" ${String(c.code).toUpperCase() === String(importSourceCRS).toUpperCase() ? "selected" : ""}>${esc(c.code)}${c.name ? ` - ${esc(c.name)}` : ""}</option>`).join("")}
        </select>
        <div class="row" style="justify-content:flex-end;">
          <button id="snd-crs-cancel" class="btn">Cancel</button>
          <button id="snd-crs-ok" class="btn primary">Import</button>
        </div>
      `;
      overlay.appendChild(card);
      document.body.appendChild(overlay);
      const close = (value) => {
        try { overlay.remove(); } catch {}
        resolve(value);
      };
      card.querySelector("#snd-crs-cancel")?.addEventListener("click", () => close(null));
      card.querySelector("#snd-crs-ok")?.addEventListener("click", () => {
        const picked = card.querySelector("#snd-crs-pick")?.value;
        close(String(picked || "").trim().toUpperCase() || null);
      });
      overlay.addEventListener("click", (e) => {
        if (e.target === overlay) close(null);
      });
    });
  }

  function fileExt(name) {
    const m = /\.([a-zA-Z0-9]+)$/.exec(String(name || "").trim());
    return m ? `.${m[1].toLowerCase()}` : "";
  }

  async function parseXYZLikeSoundings(text, sourceCRS, onProgress) {
    const rows = String(text || "").split(/\r?\n/);
    const points = [];
    let skipped = 0;
    let deduped = 0;
    const seenXY = new Set();
    const CHUNK = 2000;
    const tokenize = (line) => {
      if (line.includes(",")) return line.split(",").map((s) => String(s || "").trim().replace(/^"(.*)"$/, "$1"));
      if (line.includes("\t")) return line.split("\t").map((s) => String(s || "").trim().replace(/^"(.*)"$/, "$1"));
      return line.split(/\s+/).map((s) => String(s || "").trim().replace(/^"(.*)"$/, "$1"));
    };
    const norm = (s) => String(s || "").toLowerCase().replace(/[^a-z0-9]/g, "");
    const pickIndex = (tokens, aliases) => {
      for (let i = 0; i < tokens.length; i += 1) {
        if (aliases.has(norm(tokens[i]))) return i;
      }
      return -1;
    };
    const xAliases = new Set(["x", "lon", "lng", "long", "longitude", "easting", "utmxeasting", "xcoord", "xcoordinate"]);
    const yAliases = new Set(["y", "lat", "latitude", "northing", "utmnorthing", "ycoord", "ycoordinate"]);
    const zAliases = new Set(["z", "depth", "depthm", "elev", "elevation", "elevationm", "sounding", "height"]);
    let colMap = null; // { x, y, z }
    for (let i = 0; i < rows.length; i += 1) {
      const line = rows[i].trim();
      if (!line || line.startsWith("#") || line.startsWith("//") || line.startsWith(";")) continue;
      const parts = tokenize(line);
      if (parts.length < 3) { skipped += 1; continue; }
      if (!colMap) {
        const hasLetters = parts.some((p) => /[a-zA-Z]/.test(String(p || "")));
        if (hasLetters) {
          const xIdx = pickIndex(parts, xAliases);
          const yIdx = pickIndex(parts, yAliases);
          const zIdx = pickIndex(parts, zAliases);
          if (xIdx >= 0 && yIdx >= 0 && zIdx >= 0) {
            colMap = { x: xIdx, y: yIdx, z: zIdx };
            continue; // header row
          }
        }
        colMap = { x: 0, y: 1, z: 2 };
      }
      let x = Number(parts[colMap.x]);
      let y = Number(parts[colMap.y]);
      let z = Number(parts[colMap.z]);
      if (!Number.isFinite(x) || !Number.isFinite(y) || !Number.isFinite(z)) {
        const nums = parts
          .map((p) => Number(String(p).replace(/[^0-9eE+\-\.]/g, "")))
          .filter((n) => Number.isFinite(n));
        if (nums.length >= 3) {
          x = nums[0];
          y = nums[1];
          z = nums[2];
        }
      }
      if (!Number.isFinite(x) || !Number.isFinite(y) || !Number.isFinite(z)) { skipped += 1; continue; }
      let lon = x;
      let lat = y;
      try {
        if (String(sourceCRS).toUpperCase() !== "EPSG:4326") {
          const out = app.services?.projections?.transformCoord?.([x, y], sourceCRS, "EPSG:4326");
          lon = Number(out?.[0]);
          lat = Number(out?.[1]);
        }
      } catch {
        skipped += 1;
        continue;
      }
      if (!Number.isFinite(lat) || !Number.isFinite(lon) || Math.abs(lat) > 90 || Math.abs(lon) > 180) { skipped += 1; continue; }
      const dedupKey = `${lat.toFixed(DEDUP_DECIMALS)},${lon.toFixed(DEDUP_DECIMALS)}`;
      if (seenXY.has(dedupKey)) { deduped += 1; continue; }
      seenXY.add(dedupKey);
      points.push({ lat, lon, depth_m: Math.abs(z), raw_z: z });
      if (i > 0 && i % CHUNK === 0) {
        if (typeof onProgress === "function") onProgress(i / rows.length);
        await yieldUi();
      }
    }
    if (typeof onProgress === "function") onProgress(1);
    if (!points.length) {
      const t = String(text || "").trim().toLowerCase();
      if (t.startsWith("<!doctype html") || t.startsWith("<html") || t.includes("<head") || t.includes("<body")) {
        throw new Error("Downloaded content is HTML, not raw XYZ/CSV data.");
      }
      const firstLines = rows
        .map((r) => String(r || "").trim())
        .filter(Boolean)
        .slice(0, 4)
        .join(" | ")
        .slice(0, 260);
      throw new Error(`No valid XYZ points found. Parsed lines=${rows.length}, skipped=${skipped}. Sample: ${firstLines || "n/a"}`);
    }
    return { points, skipped, deduped };
  }

  function parseDepthCandidate(v) {
    const n = Number(v);
    if (!Number.isFinite(n)) return null;
    return Math.abs(n);
  }

  function transformXYToLatLon(x, y, sourceCRS) {
    let lon = Number(x);
    let lat = Number(y);
    if (!Number.isFinite(lon) || !Number.isFinite(lat)) return null;
    if (String(sourceCRS).toUpperCase() !== "EPSG:4326") {
      const out = app.services?.projections?.transformCoord?.([lon, lat], sourceCRS, "EPSG:4326");
      lon = Number(out?.[0]);
      lat = Number(out?.[1]);
    }
    if (!Number.isFinite(lat) || !Number.isFinite(lon) || Math.abs(lat) > 90 || Math.abs(lon) > 180) return null;
    return { lat, lon };
  }

  function parseJsonSoundings(text, sourceCRS, onProgress) {
    const payload = JSON.parse(String(text || ""));
    const candidates = [];
    const addCandidate = (x, y, z) => {
      if (!Number.isFinite(Number(x)) || !Number.isFinite(Number(y))) return;
      const depthM = parseDepthCandidate(z);
      if (!Number.isFinite(depthM)) return;
      candidates.push({ x: Number(x), y: Number(y), depth_m: Number(depthM) });
    };
    const candidateFromObj = (obj) => {
      if (!obj || typeof obj !== "object") return;
      const x = obj.lon ?? obj.lng ?? obj.longitude ?? obj.x ?? obj.X;
      const y = obj.lat ?? obj.latitude ?? obj.y ?? obj.Y;
      const z = obj.depth_m ?? obj.depth ?? obj.z ?? obj.Z ?? obj.elev ?? obj.elevation ?? obj.elevation_m;
      addCandidate(x, y, z);
    };
    const candidateFromFeature = (f) => {
      if (!f || typeof f !== "object") return;
      const g = f.geometry;
      const p = (f.properties && typeof f.properties === "object") ? f.properties : {};
      if (g && String(g.type || "").toLowerCase() === "point" && Array.isArray(g.coordinates) && g.coordinates.length >= 2) {
        const x = Number(g.coordinates[0]);
        const y = Number(g.coordinates[1]);
        const z = p.depth_m ?? p.depth ?? p.z ?? p.Z ?? p.elev ?? p.elevation ?? p.elevation_m ?? g.coordinates[2];
        addCandidate(x, y, z);
      } else {
        candidateFromObj(p);
      }
    };

    if (Array.isArray(payload)) payload.forEach(candidateFromObj);
    else if (payload?.type === "FeatureCollection" && Array.isArray(payload.features)) payload.features.forEach(candidateFromFeature);
    else if (payload?.type === "Feature") candidateFromFeature(payload);
    else if (Array.isArray(payload?.points)) payload.points.forEach(candidateFromObj);
    else candidateFromObj(payload);

    const points = [];
    let skipped = 0;
    let deduped = 0;
    const seenXY = new Set();
    const CHUNK = 1000;
    for (let i = 0; i < candidates.length; i += 1) {
      const c = candidates[i];
      let ll = null;
      try {
        ll = transformXYToLatLon(c.x, c.y, sourceCRS);
      } catch {
        ll = null;
      }
      if (!ll) { skipped += 1; continue; }
      const dedupKey = `${ll.lat.toFixed(DEDUP_DECIMALS)},${ll.lon.toFixed(DEDUP_DECIMALS)}`;
      if (seenXY.has(dedupKey)) { deduped += 1; continue; }
      seenXY.add(dedupKey);
      points.push({ lat: ll.lat, lon: ll.lon, depth_m: c.depth_m, raw_z: c.depth_m });
      if (typeof onProgress === "function" && i > 0 && i % CHUNK === 0) onProgress(i / Math.max(1, candidates.length));
    }
    if (typeof onProgress === "function") onProgress(1);
    return { points, skipped, deduped };
  }

  async function parseImportedSoundings(text, sourceCRS, onProgress, fileName = "") {
    const ext = fileExt(fileName);
    const raw = String(text || "");
    const trimmed = raw.trimStart();
    const likelyJson = ext === ".json" || ext === ".geojson" || trimmed.startsWith("{") || trimmed.startsWith("[");
    if (likelyJson) {
      try {
        return parseJsonSoundings(raw, sourceCRS, onProgress);
      } catch (err) {
        if (ext === ".json" || ext === ".geojson") throw new Error(`Invalid JSON/GeoJSON: ${err?.message || String(err)}`);
      }
    }
    return parseXYZLikeSoundings(raw, sourceCRS, onProgress);
  }

  async function importSelectedFiles() {
    if (importing) return;
    importing = true;
    try {
      const input = $("snd-import-files");
      const files = Array.from(input?.files || []);
      if (!files.length) { setStatus("select one or more XYZ/CSV/TXT/JSON files"); return; }
      const importedIds = [];
      let totalPts = 0;
      let totalSkipped = 0;
      let totalDeduped = 0;
      setImportProgress(true, 0, "Starting import...");
      for (let fi = 0; fi < files.length; fi += 1) {
        const file = files[fi];
        let text = "";
        try {
          text = await file.text();
        } catch {
          totalSkipped += 1;
          setImportProgress(true, (fi + 1) / files.length, `Failed: ${file.name}`);
          continue;
        }
        setImportProgress(true, fi / files.length, `Parsing ${file.name}...`);
        const parsed = await parseImportedSoundings(text, importSourceCRS, (fileRatio) => {
          const overall = (fi + fileRatio) / files.length;
          setImportProgress(true, overall, `Parsing ${file.name} (${Math.round(fileRatio * 100)}%)`);
        }, file.name);
        totalSkipped += parsed.skipped;
        totalDeduped += Number(parsed.deduped || 0);
        if (!parsed.points.length) continue;
        const baseName = String(file.name || "Imported").replace(/\.[^/.]+$/, "");
        const layer = createLayer({
          name: baseName || `Imported ${soundingLayers.length + 1}`,
          sourceType: "imported",
          source: `import:${importSourceCRS}`,
          sourceMeta: {
            kind: "file",
            fileName: file.name,
            sourceCRS: importSourceCRS,
            fileSize: Number(file.size) || 0,
            importedAt: new Date().toISOString(),
          },
        });
        layer.records = parsed.points.map((p) => ({
          id: `snd_${Date.now().toString(36)}_${seq++}`,
          lat: p.lat,
          lon: p.lon,
          depth_m: p.depth_m,
          ts: new Date().toISOString(),
          source: layer.source,
        }));
        syncGlobalLayer(layer);
        importedIds.push(layer.id);
        totalPts += layer.records.length;
        setImportProgress(true, (fi + 1) / files.length, `Loaded ${file.name} (${layer.records.length.toLocaleString()} pts)`);
        await yieldUi();
      }
      if (!importedIds.length) {
        setStatus("no valid rows found in selected files");
        return;
      }
      activeLayerId = importedIds[importedIds.length - 1];
      redrawSoundings();
      if (importZoom) {
        const pts = soundingLayers.filter((l) => importedIds.includes(l.id)).flatMap((l) => l.records.map((r) => [r.lat, r.lon]));
        const b = L.latLngBounds(pts);
        if (b.isValid()) map.fitBounds(b.pad(0.15));
      }
      render();
      emitActiveLayerChanged();
      emitSoundingsCatalogChanged();
      setStatus(`imported ${totalPts.toLocaleString()} points from ${importedIds.length} file(s), skipped ${totalSkipped.toLocaleString()} row(s), deduped ${totalDeduped.toLocaleString()} row(s)`);
    } finally {
      setImportProgress(false);
      importing = false;
    }
  }

  function render() {
    if (!container) return;
    const active = ensureActiveLayer(false);
    const activeStyle = layerStyleForId(active?.id);
    const hasActiveLayer = !!active;
    const canGenerate = hasActiveLayer && workflowMode === "generate";
    const canImport = hasActiveLayer && workflowMode === "import";
    const spacingEnabled = canGenerate && (mode === "section" || mode === "grid");
    container.innerHTML = `
      <div class="row" style="gap:8px; margin-bottom:8px; align-items:center;">
        <button id="snd-new-layer" class="btn snd-selected">New Sounding Layer</button>
        <span class="small">${hasActiveLayer ? `Active: ${esc(active.name)}` : "Create a layer to begin"}</span>
        <span class="muted" id="snd-status" style="margin-left:auto">${running ? "running" : "idle"}</span>
        <button id="snd-cancel-run" class="btn" ${running ? "" : "disabled"}>Cancel</button>
      </div>
      <div id="snd-gen-progress-wrap" style="display:${generationProgress.visible ? "block" : "none"}; margin-bottom:8px;">
        <div class="row" style="gap:8px; align-items:center;">
          <div style="flex:1; height:8px; border:1px solid #334155; border-radius:999px; overflow:hidden; background:#0b1220;">
            <div id="snd-gen-progress-bar" style="height:100%; width:${Math.round(Math.max(0, Math.min(1, Number(generationProgress.ratio) || 0)) * 100)}%; background:linear-gradient(90deg,#f59e0b,#f97316); transition:width 120ms linear;"></div>
          </div>
          <span id="snd-gen-progress-pct" class="small">${Math.round(Math.max(0, Math.min(1, Number(generationProgress.ratio) || 0)) * 100)}%</span>
        </div>
        <div id="snd-gen-progress-count" class="small" style="margin-top:3px;">${esc(generationProgress.countText || "0 / 0")}</div>
      </div>
      <div class="row" style="gap:8px; align-items:center; flex-wrap:wrap;">
        <button class="btn ${workflowMode === "generate" ? "snd-selected" : ""}" data-snd-workflow="generate" ${hasActiveLayer ? "" : "disabled"}>Generate</button>
        <button class="btn ${workflowMode === "import" ? "snd-selected" : ""}" data-snd-workflow="import" ${hasActiveLayer ? "" : "disabled"}>Import</button>
      </div>
      <div class="row" style="gap:12px; margin-top:8px; flex-wrap:wrap; align-items:flex-start;">
        <div style="display:flex; flex-direction:column; gap:6px;">
          <div id="snd-mode" class="modebar" style="display:flex; gap:8px;">
            <button class="snd-mode-btn ${mode === "single" ? "active" : ""}" data-mode="single" title="Point" ${canGenerate ? "" : "disabled"}>&#9679;</button>
            <button class="snd-mode-btn ${mode === "section" ? "active" : ""}" data-mode="section" title="Line" ${canGenerate ? "" : "disabled"}>&#9585;</button>
            <button class="snd-mode-btn ${mode === "grid" ? "active" : ""}" data-mode="grid" title="Grid" ${canGenerate ? "" : "disabled"}>&#9643;</button>
            <button id="snd-import-plus" class="iconbtn" title="Import soundings" aria-label="Import soundings" ${canImport ? "" : "disabled"}>+</button>
          </div>
          <div style="display:flex; flex-direction:column; gap:6px; max-width:180px;">
            <div class="row" style="gap:6px; align-items:center;">
              <label class="muted" style="min-width:52px;">Source</label>
              <select id="snd-source" ${canGenerate ? "" : "disabled"}>
                <option value="etopo" ${selectedSource === "etopo" ? "selected" : ""}>ETOPO</option>
                <option value="bluetopo" ${selectedSource === "bluetopo" ? "selected" : ""}>BlueTopo</option>
              </select>
            </div>
            <div class="row" style="gap:6px; align-items:center;">
              <label class="muted" style="min-width:52px;">Spacing</label>
              <input
                id="snd-spacing"
                type="number"
                min="0.1"
                step="0.1"
                value="${Number(spacingMeters || 5)}"
                ${spacingEnabled ? "" : "disabled"}
              />
            </div>
            <div class="row" style="gap:6px; align-items:center;">
              <label class="muted" style="min-width:52px;">Units</label>
              <select id="snd-units">
                <option value="m" ${unitMode === "m" ? "selected" : ""}>meters</option>
                <option value="ft" ${unitMode === "ft" ? "selected" : ""}>feet</option>
              </select>
            </div>
            <div class="row" style="gap:6px; align-items:center;">
              <label class="muted" style="min-width:52px;">Parallel</label>
              <input
                id="snd-concurrency"
                type="number"
                min="${MIN_SAMPLE_CONCURRENCY}"
                max="${MAX_SAMPLE_CONCURRENCY}"
                step="1"
                value="${Math.max(MIN_SAMPLE_CONCURRENCY, Math.min(MAX_SAMPLE_CONCURRENCY, Number(sampleConcurrency) || SAMPLE_CONCURRENCY))}"
                ${canGenerate ? "" : "disabled"}
              />
            </div>
          </div>
        </div>
      </div>
      <div class="row" style="gap:8px; margin-top:8px; align-items:center; flex-wrap:wrap;">
        <label class="muted">Import</label>
        <input id="snd-import-files" type="file" accept=".xyz,.txt,.csv,.json,.geojson,application/json,application/geo+json" multiple style="display:none;" />
        <label class="muted"><input type="checkbox" id="snd-import-zoom" ${importZoom ? "checked" : ""} ${canImport ? "" : "disabled"}> Zoom</label>
        <span class="small">${canImport ? "Use + to choose files." : "Select Import workflow to enable +."}</span>
      </div>
      <div id="snd-import-progress-wrap" style="display:none; margin-top:6px;">
        <div style="height:8px; border:1px solid #334155; border-radius:999px; overflow:hidden; background:#0b1220;">
          <div id="snd-import-progress-bar" style="height:100%; width:0%; background:linear-gradient(90deg,#0ea5e9,#22d3ee); transition:width 120ms linear;"></div>
        </div>
        <div id="snd-import-progress-label" class="small" style="margin-top:3px;">0%</div>
      </div>
      <div id="snd-panel-single" class="snd-mode-panel" style="${mode === "single" ? "" : "display:none;"}"><div class="muted">Point: click map to place soundings. Double-click to stop. ESC cancels.</div></div>
      <div id="snd-panel-section" class="snd-mode-panel" style="${mode === "section" ? "" : "display:none;"}"><div class="muted">Line: click vertices, double-click to finish and sample. ESC cancels.</div></div>
      <div id="snd-panel-grid" class="snd-mode-panel" style="${mode === "grid" ? "" : "display:none;"}"><div class="muted">Grid: draw polygon, double-click to finish and sample. ESC cancels.</div></div>
      <div class="row" style="gap:12px; flex-wrap:wrap; margin-top:8px;"><div><label class="muted">Font</label><br/><select id="snd-font"><option value="default" ${soundingFont === "default" ? "selected" : ""}>Default Sans</option><option value="trebuchet" ${soundingFont === "trebuchet" ? "selected" : ""}>Trebuchet</option><option value="arial" ${soundingFont === "arial" ? "selected" : ""}>Arial</option><option value="mono" ${soundingFont === "mono" ? "selected" : ""}>Consolas Mono</option><option value="mono2" ${soundingFont === "mono2" ? "selected" : ""}>Lucida Mono</option></select></div><div><label class="muted"><input type="radio" name="snd-prec" id="snd-prec-1" value="1" ${soundingPrecision === 1 ? "checked" : ""}> 1 dec</label><label class="muted" style="margin-left:8px;"><input type="radio" name="snd-prec" id="snd-prec-2" value="2" ${soundingPrecision === 2 ? "checked" : ""}> 2 dec</label></div></div>
      <div class="row" style="gap:10px; margin-top:8px; align-items:center;"><label class="muted"><input type="checkbox" id="snd-skip-land" ${skipLand ? "checked" : ""}> Skip land (MSL >= 0)</label></div>
      <div class="row" style="gap:10px; margin-top:6px; align-items:center; flex-wrap:wrap;">
        <label class="muted"><input type="checkbox" id="snd-declutter" ${declutterLabels ? "checked" : ""}> Declutter labels</label>
        <label class="muted" for="snd-declutter-sens">Sensitivity</label>
        <input id="snd-declutter-sens" type="range" min="0" max="100" step="1" value="${Math.round(Math.max(0, Math.min(100, Number(declutterSensitivity) || 50)))}" ${declutterLabels ? "" : "disabled"} style="width:140px;" />
        <span class="small" id="snd-declutter-sens-val">${Math.round(Math.max(0, Math.min(100, Number(declutterSensitivity) || 50)))}%</span>
      </div>
      <div class="row" style="gap:8px; margin-top:8px; justify-content:flex-end; align-items:center;">
        <label class="muted" for="snd-export-format">Export</label>
        <select id="snd-export-format" ${active?.records?.length ? "" : "disabled"}>
          <option value="geojson">GeoJSON</option>
          <option value="xyz">XYZ</option>
          <option value="sim_tin">Simulator TIN JSON</option>
        </select>
        <button id="snd-export" class="btn" ${active?.records?.length ? "" : "disabled"}>Export Active</button>
      </div>
      <div class="row" style="gap:10px; margin-top:8px; align-items:center;"><label class="muted" style="min-width:60px;">Rotate</label><input type="range" id="snd-rot-slider" min="0" max="359" step="1" value="${Math.round(activeStyle.rotation)}" style="flex:1;"><input type="number" id="snd-rot-num" min="0" max="359" step="1" value="${Math.round(activeStyle.rotation)}" style="width:70px;"></div>
      <div class="row" style="gap:10px; margin-top:8px; align-items:center;"><label class="muted" style="min-width:60px;">Size</label><input type="range" id="snd-size-slider" min="0.5" max="3" step="0.1" value="${activeStyle.scale}" style="flex:1;"><input type="number" id="snd-size-num" min="0.5" max="3" step="0.1" value="${activeStyle.scale}" style="width:70px;"></div>
      <div class="row" style="gap:10px; margin-top:8px; align-items:center;"><label class="muted" style="min-width:60px;">Color by</label><select id="snd-color-attr"><option value="depth" selected>Depth</option></select><select id="snd-color-scale"><option value="bathy" ${colorScale === "bathy" ? "selected" : ""}>Bathy</option><option value="viridis" ${colorScale === "viridis" ? "selected" : ""}>Viridis</option><option value="cividis" ${colorScale === "cividis" ? "selected" : ""}>Cividis</option><option value="turbo" ${colorScale === "turbo" ? "selected" : ""}>Turbo</option><option value="greys" ${colorScale === "greys" ? "selected" : ""}>Greys</option><option value="solid" ${colorScale === "solid" ? "selected" : ""}>Color</option></select><label class="muted"><input type="checkbox" id="snd-color-invert" ${colorInvert ? "checked" : ""}> Invert</label></div>
      <div class="row" style="gap:10px; align-items:center; margin-top:6px;"><label class="muted" for="snd-solid-color">Pick color</label><input id="snd-solid-color" type="color" value="${colorSolid}" /><span class="muted" style="font-size:12px;">(used when scale = Color)</span></div>
      <div class="row" style="gap:10px; align-items:center;"><label class="muted" style="min-width:60px;">Domain</label><label class="muted">Min <input id="snd-domain-min" type="number" step="0.1" style="width:80px" value="${Number.isFinite(colorDomainMin) ? colorDomainMin : ""}" placeholder="auto"></label><label class="muted">Max <input id="snd-domain-max" type="number" step="0.1" style="width:80px" value="${Number.isFinite(colorDomainMax) ? colorDomainMax : ""}" placeholder="auto"></label><label class="muted"><input type="checkbox" id="snd-domain-auto" ${colorDomainAuto ? "checked" : ""}> Auto</label><button id="snd-apply-colors" class="btn">Apply</button></div>
      <div class="row" style="gap:10px; align-items:center;"><label class="muted"><input type="checkbox" id="snd-clamp" ${colorClamp ? "checked" : ""}> Clamp</label><label class="muted"><input type="checkbox" id="snd-gamma-on" ${colorGamma ? "checked" : ""}> Emphasize shoals</label></div>
      <div id="snd-legend-wrap" style="margin-top:6px;"><div id="snd-legend" style="height:12px;border:1px solid #334155;border-radius:6px;overflow:hidden;background:linear-gradient(90deg,#0b1220,#e5e7eb);"></div><div id="snd-legend-ticks" style="display:flex;justify-content:space-between;font-size:10px;opacity:0.8;margin-top:2px;"><span id="snd-legend-min">min</span><span id="snd-legend-q1"></span><span id="snd-legend-mid">mid</span><span id="snd-legend-q3"></span><span id="snd-legend-max">max</span></div></div>
      <div class="soundings-summary small" style="margin-top:8px;">${active?.records?.length || 0} active / ${totalCount()} total soundings</div>
    `;

    el.status = $("snd-status"); el.source = $("snd-source"); el.importZoom = $("snd-import-zoom"); el.spacing = $("snd-spacing"); el.units = $("snd-units"); el.concurrency = $("snd-concurrency"); el.font = $("snd-font"); el.skipLand = $("snd-skip-land");
    el.declutter = $("snd-declutter"); el.declutterSens = $("snd-declutter-sens"); el.declutterSensVal = $("snd-declutter-sens-val");
    el.prec1 = $("snd-prec-1"); el.prec2 = $("snd-prec-2"); el.rotSlider = $("snd-rot-slider"); el.rotNum = $("snd-rot-num"); el.sizeSlider = $("snd-size-slider"); el.sizeNum = $("snd-size-num");
    el.colorScale = $("snd-color-scale"); el.colorInvert = $("snd-color-invert"); el.colorSolid = $("snd-solid-color"); el.domainMin = $("snd-domain-min"); el.domainMax = $("snd-domain-max"); el.domainAuto = $("snd-domain-auto"); el.clamp = $("snd-clamp"); el.gamma = $("snd-gamma-on"); el.applyColors = $("snd-apply-colors"); el.exportFormat = $("snd-export-format");

    if (el.source) el.source.onchange = () => {
      const next = String(el.source.value || "etopo").toLowerCase();
      selectedSource = next === "bluetopo" ? "bluetopo" : "etopo";
      setStatus(`source: ${selectedSource === "bluetopo" ? "BlueTopo" : "ETOPO"}`);
    };
    const importFiles = $("snd-import-files");
    if (importFiles) {
      importFiles.onchange = async () => {
        const files = Array.from(importFiles.files || []);
        if (!files.length) return;
        const picked = await promptImportCrsSelect();
        if (!picked) {
          importFiles.value = "";
          setStatus("import cancelled");
          return;
        }
        importSourceCRS = picked;
        await importSelectedFiles();
      };
    }
    if (el.importZoom) el.importZoom.onchange = () => { importZoom = !!el.importZoom.checked; };
    if (el.spacing) {
      const commitSpacing = () => {
        spacingMeters = Math.max(0.1, Number(el.spacing.value || 5));
        if (Number(el.spacing.value) !== spacingMeters) el.spacing.value = String(spacingMeters);
      };
      el.spacing.oninput = commitSpacing;
      el.spacing.onchange = commitSpacing;
    }
    if (el.units) el.units.onchange = () => { unitMode = el.units.value || "m"; redrawSoundings(); };
    if (el.concurrency) {
      const commitConcurrency = () => {
        sampleConcurrency = Math.max(MIN_SAMPLE_CONCURRENCY, Math.min(MAX_SAMPLE_CONCURRENCY, Math.round(Number(el.concurrency.value) || SAMPLE_CONCURRENCY)));
        el.concurrency.value = String(sampleConcurrency);
      };
      el.concurrency.oninput = commitConcurrency;
      el.concurrency.onchange = commitConcurrency;
    }
    if (el.font) el.font.onchange = () => { soundingFont = el.font.value || "default"; redrawSoundings(); setStatus(`font: ${soundingFont}`); };
    if (el.skipLand) el.skipLand.onchange = () => { skipLand = !!el.skipLand.checked; };
    if (el.declutter) {
      el.declutter.onchange = () => {
        declutterLabels = !!el.declutter.checked;
        if (el.declutterSens) el.declutterSens.disabled = !declutterLabels;
        redrawSoundings();
      };
    }
    if (el.declutterSens) {
      const onDeclutterSens = () => {
        declutterSensitivity = Math.max(0, Math.min(100, Number(el.declutterSens.value) || 0));
        if (el.declutterSensVal) el.declutterSensVal.textContent = `${Math.round(declutterSensitivity)}%`;
        redrawSoundings();
      };
      el.declutterSens.oninput = onDeclutterSens;
      el.declutterSens.onchange = onDeclutterSens;
    }
    if (el.prec1) el.prec1.onchange = () => { soundingPrecision = 1; redrawSoundings(); };
    if (el.prec2) el.prec2.onchange = () => { soundingPrecision = 2; redrawSoundings(); };
    if (el.rotSlider) el.rotSlider.oninput = () => setRotation(el.rotSlider.value);
    if (el.rotNum) el.rotNum.onchange = () => setRotation(el.rotNum.value);
    if (el.sizeSlider) el.sizeSlider.oninput = () => setSize(el.sizeSlider.value);
    if (el.sizeNum) el.sizeNum.onchange = () => setSize(el.sizeNum.value);
    if (el.colorScale) el.colorScale.onchange = () => { colorScale = el.colorScale.value || "bathy"; applySoundingColors(); };
    if (el.colorInvert) el.colorInvert.onchange = () => { colorInvert = !!el.colorInvert.checked; applySoundingColors(); };
    if (el.colorSolid) el.colorSolid.onchange = () => { colorSolid = el.colorSolid.value || "#00ffff"; applySoundingColors(); };
    if (el.domainAuto) el.domainAuto.onchange = () => { colorDomainAuto = !!el.domainAuto.checked; if (colorDomainAuto) { colorDomainMin = null; colorDomainMax = null; } applySoundingColors(); };
    if (el.clamp) el.clamp.onchange = () => { colorClamp = !!el.clamp.checked; applySoundingColors(); };
    if (el.gamma) el.gamma.onchange = () => { colorGamma = !!el.gamma.checked; applySoundingColors(); };
    if (el.applyColors) el.applyColors.onclick = () => { const min = Number(el.domainMin?.value); const max = Number(el.domainMax?.value); if (!colorDomainAuto && Number.isFinite(min) && Number.isFinite(max) && min < max) { colorDomainMin = min; colorDomainMax = max; } applySoundingColors(); };
    $("snd-cancel-run")?.addEventListener("click", () => {
      if (!running) return;
      running = false;
      setStatus("cancelling...");
    });
    applySoundingColors();
    emitActiveLayerChanged();
  }

  function onClick(e) {
    if (e.target?.id === "snd-import-plus") {
      if (workflowMode !== "import") { setStatus("select Import mode"); return; }
      if (!getActiveLayer()) { setStatus("create a sounding layer first"); return; }
      $("snd-import-files")?.click();
      return;
    }
    const wfBtn = e.target?.closest?.("[data-snd-workflow]");
    if (wfBtn) {
      workflowMode = wfBtn.getAttribute("data-snd-workflow") === "import" ? "import" : "generate";
      mode = "";
      stopInteraction(false);
      render();
      setStatus(workflowMode === "import" ? "import mode ready" : "generate mode ready");
      return;
    }
    const mbtn = e.target?.closest?.(".snd-mode-btn");
    if (mbtn) {
      if (workflowMode !== "generate") { setStatus("select Generate mode"); return; }
      mode = mbtn.dataset.mode || "single";
      stopInteraction(false);
      render();
      renderModeButtons();
      beginInteraction();
      setStatus(mode === "single" ? "point mode active" : mode === "section" ? "line mode active" : "grid mode active");
      return;
    }
    if (e.target?.id === "snd-new-layer") { activeLayerId = createLayer({ name: `Soundings ${soundingLayers.length + 1}`, source: selectedSource, sourceType: "generated" }).id; workflowMode = ""; mode = ""; render(); setStatus("new layer created"); return; }
    if (e.target?.id === "snd-export") { exportActive(); return; }
  }

  function mount(next) {
    if (container === next) return;
    container = next;
    render();
    redrawSoundings();
    syncStyleControlsFromActiveLayer();
    container.addEventListener("click", onClick);
    offActiveSelect = app.events.on("soundings:active-layer:set", ({ globalLayerId } = {}) => {
      if (!globalLayerId) return;
      const layer = soundingLayers.find((l) => l.globalLayerId === globalLayerId);
      if (!layer) return;
      activeLayerId = layer.id;
      render();
      syncStyleControlsFromActiveLayer();
      emitSoundingsCatalogChanged();
      setStatus("active layer selected");
    });
    offLayerRename = app.events.on("soundings:layer:rename", ({ globalLayerId, name } = {}) => {
      const nextName = String(name || "").trim();
      if (!globalLayerId || !nextName) return;
      const layer = soundingLayers.find((l) => l.globalLayerId === globalLayerId);
      if (!layer) return;
      layer.name = nextName;
      syncGlobalLayer(layer);
      render();
      emitSoundingsCatalogChanged();
      setStatus("layer renamed");
    });
    offCatalogRequest = app.events.on("soundings:catalog:request", () => {
      emitSoundingsCatalogChanged();
    });
    const onView = () => redrawSoundings();
    map.on("moveend", onView);
    map.on("zoomend", onView);
    viewRefreshOff = () => { map.off("moveend", onView); map.off("zoomend", onView); };
    emitSoundingsCatalogChanged();
  }

  offExternalImport = app.events.on("soundings:import:text", async (payload = {}) => {
    const resolve = typeof payload?.resolve === "function" ? payload.resolve : null;
    const reject = typeof payload?.reject === "function" ? payload.reject : null;
    try {
      const out = await importExternalText(payload);
      setStatus(`imported ${Number(out?.pointCount || 0).toLocaleString()} points`);
      if (resolve) resolve(out);
    } catch (err) {
      if (reject) reject(err);
    }
  });

  function snapshot(options = {}) {
    const dataPolicy = String(options?.dataPolicy || "reference");
    const embedMax = Math.max(0, Number(options?.embedMaxSoundingPoints || 0) || 0);
    return {
      mode, workflowMode, soundingPrecision, soundingScale, soundingRotation, selectedSource, spacingMeters, sampleConcurrency, unitMode, skipLand, soundingFont, declutterLabels, declutterSensitivity,
      writeMode,
      renderMode,
      importSourceCRS, importZoom,
      colorScale, colorInvert, colorClamp, colorGamma, colorDomainAuto, colorDomainMin, colorDomainMax, colorSolid,
      activeLayerId,
      dataPolicy,
      soundingLayers: soundingLayers.map((layer) => {
        const embedAll = dataPolicy === "embed";
        const inlineSmall = !embedAll && layer.records.length <= REFERENCE_INLINE_LIMIT;
        const includeRecords = embedAll || inlineSmall;
        const stride = embedAll && embedMax > 0 && layer.records.length > embedMax
          ? Math.ceil(layer.records.length / embedMax)
          : 1;
        const sampledRecords = includeRecords
          ? layer.records.filter((_, idx) => (idx % stride) === 0)
          : [];
        return {
          id: layer.id,
          name: layer.name,
          sourceType: layer.sourceType,
          source: layer.source,
          sourceMeta: layer.sourceMeta ? { ...layer.sourceMeta } : null,
          visible: layer.visible,
          opacity: layer.opacity ?? 1,
          labelRotation: Number.isFinite(Number(layer.labelRotation)) ? Number(layer.labelRotation) : 0,
          labelScale: Number.isFinite(Number(layer.labelScale)) ? Number(layer.labelScale) : 1,
          pointCount: layer.records.length,
          savedPointCount: sampledRecords.length,
          recordsMode: includeRecords ? (embedAll ? "embedded" : "inline-small") : "reference",
          unresolvedSource: !includeRecords,
          records: sampledRecords.map((r) => ({ id: r.id, lat: r.lat, lon: r.lon, depth_m: r.depth_m, ts: r.ts, source: r.source })),
        };
      }),
    };
  }

  function restore(data) {
    stopInteraction(false);
    if (soundingLayers.length) {
      inGlobalSync = true;
      try {
        soundingLayers.forEach((layer) => {
          if (!layer?.globalLayerId) return;
          try { app.services.layers.removeLayer(layer.globalLayerId); } catch {}
        });
      } finally {
        inGlobalSync = false;
      }
    }
    soundingLayer.clearLayers();
    soundingLayers = [];
    unresolvedRefs.clear();
    mode = ["single", "section", "grid"].includes(data?.mode) ? data.mode : "";
    workflowMode = data?.workflowMode === "import" ? "import" : (data?.workflowMode === "generate" ? "generate" : "");
    soundingPrecision = data?.soundingPrecision === 2 ? 2 : 1;
    spacingMeters = Number.isFinite(Number(data?.spacingMeters))
      ? Math.max(0.1, Number(data.spacingMeters))
      : 5;
    sampleConcurrency = Number.isFinite(Number(data?.sampleConcurrency))
      ? Math.max(MIN_SAMPLE_CONCURRENCY, Math.min(MAX_SAMPLE_CONCURRENCY, Math.round(Number(data.sampleConcurrency))))
      : SAMPLE_CONCURRENCY;
    unitMode = ["m", "ft"].includes(data?.unitMode) ? data.unitMode : "m";
    skipLand = data?.skipLand !== false;
    declutterLabels = data?.declutterLabels !== false;
    declutterSensitivity = Math.max(0, Math.min(100, Number(data?.declutterSensitivity) || 50));
    soundingFont = ["default", "trebuchet", "arial", "mono", "mono2"].includes(data?.soundingFont) ? data.soundingFont : "default";
    soundingScale = Number(data?.soundingScale || 1);
    soundingRotation = Number(data?.soundingRotation || 0);
    selectedSource = String(data?.selectedSource || "etopo").toLowerCase() === "bluetopo" ? "bluetopo" : "etopo";
    writeMode = data?.writeMode === "replace" ? "replace" : "append";
    renderMode = data?.renderMode === "points" || data?.renderMode === "labels" ? data.renderMode : "auto";
    importSourceCRS = typeof data?.importSourceCRS === "string" && data.importSourceCRS.trim() ? data.importSourceCRS : "EPSG:4326";
    importZoom = data?.importZoom !== false;
    colorScale = data?.colorScale || "bathy"; colorInvert = !!data?.colorInvert; colorClamp = data?.colorClamp !== false; colorGamma = !!data?.colorGamma; colorDomainAuto = data?.colorDomainAuto !== false; colorDomainMin = Number.isFinite(Number(data?.colorDomainMin)) ? Number(data.colorDomainMin) : null; colorDomainMax = Number.isFinite(Number(data?.colorDomainMax)) ? Number(data.colorDomainMax) : null; colorSolid = typeof data?.colorSolid === "string" ? data.colorSolid : "#00ffff";
    let unresolvedCount = 0;
    if (Array.isArray(data?.soundingLayers) && data.soundingLayers.length) {
      data.soundingLayers.forEach((layer) => {
        const records = (Array.isArray(layer.records) ? layer.records : []).map((r) => ({ id: r.id || `snd_${Date.now().toString(36)}_${seq++}`, lat: Number(r.lat), lon: Number(r.lon), depth_m: Number(r.depth_m), ts: r.ts || new Date().toISOString(), source: r.source || layer.source || "etopo" }));
        if (!records.length && Number(layer.pointCount) > 0) {
          unresolvedCount += 1;
          unresolvedRefs.set(String(layer.id), {
            id: String(layer.id),
            name: String(layer.name || "Soundings"),
            pointCount: Number(layer.pointCount) || 0,
            sourceMeta: layer.sourceMeta && typeof layer.sourceMeta === "object" ? { ...layer.sourceMeta } : {},
          });
        }
        createLayer({
          id: layer.id,
          name: layer.name,
          sourceType: layer.sourceType || "generated",
          source: layer.source || "etopo",
          sourceMeta: layer.sourceMeta && typeof layer.sourceMeta === "object" ? layer.sourceMeta : null,
          visible: layer.visible !== false,
          opacity: Number.isFinite(Number(layer.opacity)) ? Number(layer.opacity) : 1,
          labelRotation: Number.isFinite(Number(layer.labelRotation)) ? Number(layer.labelRotation) : undefined,
          labelScale: Number.isFinite(Number(layer.labelScale)) ? Number(layer.labelScale) : undefined,
          records,
        });
      });
      activeLayerId = data?.activeLayerId || soundingLayers[0]?.id || null;
    } else {
      const legacy = Array.isArray(data?.records) ? data.records : [];
      const layer = createLayer({ name: "Soundings 1", source: "etopo", sourceType: "generated" });
      layer.records = legacy.map((r) => ({ id: r.id || `snd_${Date.now().toString(36)}_${seq++}`, lat: Number(r.lat), lon: Number(r.lon), depth_m: Number(r.depth_m), ts: r.ts || new Date().toISOString(), source: "etopo" }));
      activeLayerId = layer.id;
    }
    render();
    redrawSoundings();
    syncStyleControlsFromActiveLayer();
    emitSoundingsCatalogChanged();
    if (unresolvedCount > 0) app.ui.toast(`Soundings restore: ${unresolvedCount} layer(s) need source relink`);
    return {
      restored: soundingLayers.length - unresolvedCount,
      unresolved: [...unresolvedRefs.values()].map((r) => ({
        toolId: "etopo-sounding",
        referenceId: r.id,
        label: `${r.name} (${r.pointCount.toLocaleString()} pts)`,
        kind: "soundings",
        hint: `Select source file${r.sourceMeta?.sourceCRS ? ` (${r.sourceMeta.sourceCRS})` : ""} to relink.`,
        expectedFileName: String(r.sourceMeta?.fileName || "").trim() || null,
      })),
    };
  }

  async function resolveReference(referenceId, file) {
    const refId = String(referenceId || "");
    const ref = unresolvedRefs.get(refId);
    if (!ref) throw new Error("Reference not found.");
    if (!file) throw new Error("Choose a file to relink.");
    const layer = layerById(refId);
    if (!layer) throw new Error("Target sounding layer not found.");
    const sourceCRS = String(ref.sourceMeta?.sourceCRS || importSourceCRS || "EPSG:4326");
    const text = await file.text();
    const parsed = await parseImportedSoundings(text, sourceCRS, null, file?.name || "");
    if (!parsed?.points?.length) throw new Error("No valid points in selected file.");
    layer.records = parsed.points.map((p) => ({
      id: `snd_${Date.now().toString(36)}_${seq++}`,
      lat: p.lat,
      lon: p.lon,
      depth_m: p.depth_m,
      ts: new Date().toISOString(),
      source: layer.source || `import:${sourceCRS}`,
    }));
    layer.sourceType = "imported";
    layer.source = `import:${sourceCRS}`;
    layer.sourceMeta = {
      kind: "file",
      fileName: file.name,
      sourceCRS,
      fileSize: Number(file.size) || 0,
      importedAt: new Date().toISOString(),
    };
    unresolvedRefs.delete(refId);
    syncGlobalLayer(layer);
    redrawSoundings();
    render();
    emitSoundingsCatalogChanged();
    app.ui.toast(`Relinked soundings: ${layer.name}`);
    return { restored: 1 };
  }

  async function importExternalText(payload = {}) {
    const progress = typeof payload?.progress === "function" ? payload.progress : null;
    const text = String(payload?.text ?? "");
    if (!text.trim()) throw new Error("No text data provided.");
    if (progress) progress({ stage: "parse", ratio: 0, message: "Parsing imported soundings..." });
    const hintLat = Number(payload?.hintLat);
    const hintLon = Number(payload?.hintLon);
    let sourceCRS = String(payload?.sourceCRS || "EPSG:4326").trim() || "EPSG:4326";
    sourceCRS = chooseImportCrs(text, sourceCRS, hintLat, hintLon);
    if (progress) progress({ stage: "parse", ratio: 0.02, message: `Using CRS ${sourceCRS}` });
    const fileName = String(payload?.fileName || payload?.name || "ehydro.xyz");
    const layerNameBase = String(payload?.layerName || payload?.name || "eHydro Import").trim() || "eHydro Import";
    const sourceUrl = String(payload?.sourceUrl || "").trim();
    const importSig = `${layerNameBase}|${sourceUrl}|${sourceCRS}`.toLowerCase();
    const nowTs = Date.now();
    if (importSig && importSig === lastExternalImportSig && (nowTs - lastExternalImportTs) < 2500) {
      const active = getActiveLayer();
      return {
        layerId: active?.id || null,
        layerName: active?.name || layerNameBase,
        pointCount: Number(active?.records?.length || 0),
        skipped: 0,
        deduped: 0,
      };
    }
    lastExternalImportSig = importSig;
    lastExternalImportTs = nowTs;
    const parsed = await parseImportedSoundings(text, sourceCRS, (r) => {
      if (progress) progress({ stage: "parse", ratio: Math.max(0, Math.min(1, Number(r) || 0)), message: `Parsing imported soundings (${Math.round((Number(r) || 0) * 100)}%)...` });
    }, fileName);
    if (!parsed?.points?.length) throw new Error("No valid XYZ points found.");
    if (progress) progress({ stage: "build", ratio: 0.9, message: "Creating sounding layer..." });
    const existing = soundingLayers.find((l) => (
      String(l?.sourceType || "") === "imported"
      && String(l?.sourceMeta?.kind || "") === "remote"
      && String(l?.sourceMeta?.sourceUrl || "") === sourceUrl
    )) || null;
    const layer = existing || createLayer({
      name: `${layerNameBase} (${new Date().toISOString().slice(0, 10)})`,
      sourceType: "imported",
      source: `import:${sourceCRS}`,
      sourceMeta: {
        kind: "remote",
        sourceCRS,
        importedAt: new Date().toISOString(),
        sourceUrl,
        fileName,
      },
    });
    layer.name = `${layerNameBase} (${new Date().toISOString().slice(0, 10)})`;
    layer.sourceType = "imported";
    layer.source = `import:${sourceCRS}`;
    layer.sourceMeta = {
      kind: "remote",
      sourceCRS,
      importedAt: new Date().toISOString(),
      sourceUrl,
      fileName,
    };
    layer.records = parsed.points.map((p) => ({
      id: `snd_${Date.now().toString(36)}_${seq++}`,
      lat: p.lat,
      lon: p.lon,
      depth_m: p.depth_m,
      ts: new Date().toISOString(),
      source: layer.source,
    }));
    activeLayerId = layer.id;
    renderMode = "labels";
    syncGlobalLayer(layer);
    redrawSoundings();
    render();
    emitActiveLayerChanged();
    emitSoundingsCatalogChanged();
    if (payload?.zoom !== false) {
      const b = L.latLngBounds(layer.records.map((r) => [r.lat, r.lon]));
      if (b.isValid()) map.fitBounds(b.pad(0.12));
    }
    if (progress) progress({ stage: "done", ratio: 1, message: `Imported ${layer.records.length.toLocaleString()} points.` });
    return {
      layerId: layer.id,
      layerName: layer.name,
      pointCount: layer.records.length,
      skipped: Number(parsed?.skipped || 0),
      deduped: Number(parsed?.deduped || 0),
    };
  }

  function isLikelyHorizontalCrs(code) {
    const c = String(code || "").toUpperCase();
    const m = c.match(/EPSG:(\d{3,6})/);
    if (!m) return c === "EPSG:4326";
    const n = Number(m[1]);
    if (!Number.isFinite(n)) return false;
    if (n === 4326 || n === 3857 || n === 3395) return true;
    if (n >= 2000 && n <= 3999) return true;
    if (n >= 10000 && n <= 39999) return true;
    return false;
  }

  function meanErrorForCrs(samples, code, targetLat, targetLon) {
    const projectionSvc = app.services?.projections;
    if (!samples.length || !projectionSvc) return Infinity;
    let valid = 0;
    let err = 0;
    for (const [x, y] of samples) {
      let out = null;
      try {
        out = String(code).toUpperCase() === "EPSG:4326"
          ? [x, y]
          : projectionSvc.transformCoord([x, y], code, "EPSG:4326");
      } catch {
        out = null;
      }
      const llon = Number(out?.[0]);
      const llat = Number(out?.[1]);
      if (!Number.isFinite(llat) || !Number.isFinite(llon) || Math.abs(llat) > 90 || Math.abs(llon) > 180) continue;
      valid += 1;
      if (Number.isFinite(targetLat) && Number.isFinite(targetLon)) {
        const dx = (llon - targetLon) * Math.cos((targetLat * Math.PI) / 180);
        const dy = (llat - targetLat);
        err += Math.sqrt((dx * dx) + (dy * dy));
      }
    }
    if (!valid) return Infinity;
    return Number.isFinite(targetLat) && Number.isFinite(targetLon) ? (err / valid) : 0;
  }

  function chooseImportCrs(text, requestedCRS, hintLat, hintLon) {
    const req = String(requestedCRS || "EPSG:4326").toUpperCase();
    const rows = String(text || "").split(/\r?\n/).slice(0, 120);
    const samples = [];
    for (const row of rows) {
      const line = String(row || "").trim();
      if (!line || line.startsWith("#") || line.startsWith("//") || line.startsWith(";")) continue;
      const nums = line
        .split(/[,\s\t]+/)
        .map((p) => Number(String(p).replace(/[^0-9eE+\-\.]/g, "")))
        .filter((n) => Number.isFinite(n));
      if (nums.length < 2) continue;
      samples.push([nums[0], nums[1]]);
      if (samples.length >= 20) break;
    }
    if (!samples.length) return req;
    const looksGeographic = samples.every(([x, y]) => Math.abs(x) <= 180 && Math.abs(y) <= 90);
    if (looksGeographic) return "EPSG:4326";
    const inCalifornia = Number.isFinite(hintLat) && Number.isFinite(hintLon)
      && hintLat >= 31.5 && hintLat <= 42.5
      && hintLon >= -125.5 && hintLon <= -113.5;
    if (inCalifornia) {
      // Deterministic CA override:
      // State Plane CA ftUS eastings are typically ~4M-8.5M, northings ~0.5M-3.5M.
      const xs = samples.map((s) => Number(s[0])).filter(Number.isFinite);
      const ys = samples.map((s) => Number(s[1])).filter(Number.isFinite);
      const med = (arr) => {
        if (!arr.length) return NaN;
        const a = [...arr].sort((a, b) => a - b);
        return a[Math.floor(a.length / 2)];
      };
      const mx = med(xs);
      const my = med(ys);
      const likelyFeet = Number.isFinite(mx) && Number.isFinite(my)
        && mx >= 4_000_000 && mx <= 8_800_000
        && my >= 300_000 && my <= 3_600_000;
      const ftCodes = ["EPSG:2225", "EPSG:2226", "EPSG:2227", "EPSG:2228", "EPSG:2229", "EPSG:2230"];
      const mCodes = ["EPSG:26941", "EPSG:26942", "EPSG:26943", "EPSG:26944", "EPSG:26945", "EPSG:26946"];
      const pool = likelyFeet ? ftCodes : mCodes;
      let best = { code: pool[0], err: Infinity };
      for (const code of pool) {
        const e = meanErrorForCrs(samples, code, hintLat, hintLon);
        if (Number.isFinite(e) && e < best.err) best = { code, err: e };
      }
      if (Number.isFinite(best.err)) return best.code;
    }
    // Trust requested CRS only if it looks horizontal and lands near click hint.
    if (isLikelyHorizontalCrs(req)) {
      const e = meanErrorForCrs(samples, req, hintLat, hintLon);
      if (e <= 0.35) return req; // ~<= 39 km equivalent angular error
    }
    return autoDetectProjectedCrsForText(text, "EPSG:4326", hintLat, hintLon);
  }

  function autoDetectProjectedCrsForText(text, requestedCRS, hintLat, hintLon) {
    const req = String(requestedCRS || "EPSG:4326").toUpperCase();
    if (req !== "EPSG:4326") return req;
    const rows = String(text || "").split(/\r?\n/).slice(0, 120);
    const samples = [];
    for (const row of rows) {
      const line = String(row || "").trim();
      if (!line || line.startsWith("#") || line.startsWith("//") || line.startsWith(";")) continue;
      const nums = line
        .split(/[,\s\t]+/)
        .map((p) => Number(String(p).replace(/[^0-9eE+\-\.]/g, "")))
        .filter((n) => Number.isFinite(n));
      if (nums.length < 2) continue;
      samples.push([nums[0], nums[1]]);
      if (samples.length >= 20) break;
    }
    if (!samples.length) return req;
    const looksGeographic = samples.every(([x, y]) => Math.abs(x) <= 180 && Math.abs(y) <= 90);
    if (looksGeographic) return "EPSG:4326";
    const projectionSvc = app.services?.projections;
    const listed = typeof projectionSvc?.listCRS === "function" ? projectionSvc.listCRS() : [];
    const listedCodes = listed.map((c) => String(c?.code || "").toUpperCase()).filter(Boolean);
    const lon = Number(hintLon);
    const lat = Number(hintLat);
    const utmZone = Number.isFinite(lon) ? Math.max(1, Math.min(60, Math.floor((lon + 180) / 6) + 1)) : null;
    const utmNorth = utmZone ? `EPSG:${32600 + utmZone}` : null;
    const utmSouth = utmZone ? `EPSG:${32700 + utmZone}` : null;
    const inCalifornia = Number.isFinite(lat) && Number.isFinite(lon)
      && lat >= 31.5 && lat <= 42.5
      && lon >= -125.5 && lon <= -113.5;
    const californiaCandidates = [
      "EPSG:2225", "EPSG:2226", "EPSG:2227", "EPSG:2228", "EPSG:2229", "EPSG:2230",
      "EPSG:26941", "EPSG:26942", "EPSG:26943", "EPSG:26944", "EPSG:26945", "EPSG:26946",
      "EPSG:26910", "EPSG:26911", "EPSG:32610", "EPSG:32611",
    ];
    const generalCandidates = [
      "EPSG:3857",
      "EPSG:3395",
      utmNorth,
      utmSouth,
      ...listedCodes,
    ];
    const candidates = (inCalifornia ? californiaCandidates : generalCandidates)
      .filter(Boolean)
      .filter((v, i, a) => a.indexOf(v) === i)
      .slice(0, 700);
    let best = { code: "EPSG:4326", score: -Infinity, avgErr: Infinity, valid: 0 };
    const targetLat = Number.isFinite(lat) ? lat : null;
    const targetLon = Number.isFinite(lon) ? lon : null;
    for (const code of candidates) {
      let valid = 0;
      let err = 0;
      for (const [x, y] of samples) {
        let out = null;
        try {
          out = String(code).toUpperCase() === "EPSG:4326"
            ? [x, y]
            : projectionSvc?.transformCoord?.([x, y], code, "EPSG:4326");
        } catch {
          out = null;
        }
        const llon = Number(out?.[0]);
        const llat = Number(out?.[1]);
        if (!Number.isFinite(llat) || !Number.isFinite(llon) || Math.abs(llat) > 90 || Math.abs(llon) > 180) continue;
        valid += 1;
        if (targetLat != null && targetLon != null) {
          const dx = (llon - targetLon) * Math.cos((targetLat * Math.PI) / 180);
          const dy = (llat - targetLat);
          err += Math.sqrt((dx * dx) + (dy * dy));
        }
      }
      if (!valid) continue;
      const avgErr = (targetLat != null && targetLon != null) ? (err / valid) : 0;
      // Prioritize spatial closeness first; valid count is only a tie-breaker.
      // 0.1 deg is ~11 km latitude, so this strongly disfavors distant projections.
      const score = (100 / (0.01 + avgErr)) + (valid * 0.01);
      if (score > best.score) best = { code, score, avgErr, valid };
    }
    if (targetLat != null && targetLon != null) {
      // If best CRS still lands far from click location, prefer known regional fallbacks.
      if (!Number.isFinite(best.avgErr) || best.avgErr > 1.0) {
        const regional = [
          "EPSG:2225", "EPSG:2226", "EPSG:2227", "EPSG:2228", "EPSG:2229", "EPSG:2230",
          "EPSG:26941", "EPSG:26942", "EPSG:26943", "EPSG:26944", "EPSG:26945", "EPSG:26946",
        ];
        let fallback = { code: req, avgErr: Infinity, valid: 0 };
        for (const code of regional) {
          let valid = 0;
          let err = 0;
          for (const [x, y] of samples) {
            let out = null;
            try { out = projectionSvc?.transformCoord?.([x, y], code, "EPSG:4326"); } catch { out = null; }
            const llon = Number(out?.[0]);
            const llat = Number(out?.[1]);
            if (!Number.isFinite(llat) || !Number.isFinite(llon) || Math.abs(llat) > 90 || Math.abs(llon) > 180) continue;
            valid += 1;
            const dx = (llon - targetLon) * Math.cos((targetLat * Math.PI) / 180);
            const dy = (llat - targetLat);
            err += Math.sqrt((dx * dx) + (dy * dy));
          }
          if (!valid) continue;
          const avgErr = err / valid;
          if (avgErr < fallback.avgErr) fallback = { code, avgErr, valid };
        }
        if (Number.isFinite(fallback.avgErr) && fallback.avgErr < best.avgErr) return fallback.code;
      }
    }
    if (targetLat != null && targetLon != null) {
      // Hard guardrail: reject distant solutions.
      if (!Number.isFinite(best.avgErr) || best.avgErr > 2.0) return req;
    }
    return best.code || req;
  }

  function destroy() {
    stopInteraction(false);
    try { viewRefreshOff?.(); } catch {}
    try { offActiveSelect?.(); } catch {}
    try { offLayerRename?.(); } catch {}
    try { offCatalogRequest?.(); } catch {}
    try { offExternalImport?.(); } catch {}
    offActiveSelect = null;
    offLayerRename = null;
    offCatalogRequest = null;
    offExternalImport = null;
    viewRefreshOff = null;
    if (container) container.removeEventListener("click", onClick);
    if (soundingLayers.length) {
      inGlobalSync = true;
      try {
        soundingLayers.forEach((layer) => {
          if (!layer?.globalLayerId) return;
          try { app.services.layers.removeLayer(layer.globalLayerId); } catch {}
        });
      } finally {
        inGlobalSync = false;
      }
    }
    soundingLayer.clearLayers();
    drawLayer.clearLayers();
    soundingLayers = [];
    emitSoundingsCatalogChanged();
    try { pointCanvasLayer?.remove?.(); } catch {}
    pointCanvasLayer = null;
    try { map.removeLayer(drawLayer); } catch {}
    try { map.removeLayer(soundingLayer); } catch {}
    container = null;
  }

  return { mount, destroy, snapshot, restore, resolveReference };
}
